/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-001
 * PURPOSE: Canonical body for the email-hub data layer — query hooks
 *          (useEmails, useEmail, useEmailJobLink, useLinkEmail,
 *          useEmailTags, useEmailMetadata, useEmailSyncStatus,
 *          useEmailFolders, useEmailContacts, useEmailRules), the
 *          shared `Email` / `EmailAttachment` / `EmailListParams`
 *          / `EmailFilters` / `SuggestedJob` types, and the action
 *          mutations (assign, archive, delete, reject_job, etc).
 *
 * WHY:     Both tenants (apps/sam, apps/newmark) and the master SPA
 *          (Bravada) consume the email feature through this single
 *          canonical body via Vite alias `@quotech/email-hub` →
 *          `packages/email-hub/src/`. The 96-file tenant feature
 *          tree under `apps/<tenant>/src/features/email/` is now
 *          shim-only (closed in MR-H, qf!3096); a fix here lands on
 *          every tenant on the next build with no mirror step.
 *
 *          The 7 setter-based DI seams it relies on
 *          (`setEmailTenantId`, `setEmailBasePath`,
 *          `setKanbanUseApiAuth`, `setKanbanUseToast`, the
 *          `<ToastProvider/>` and `<ApiAuthGate/>` providers, and
 *          the `kanbanOrgIdResolver` registry) are wired once per
 *          tenant at module-eval time in
 *          `apps/<tenant>/src/main.tsx`. The hook bodies below never
 *          read tenant-relative paths — that's the contract that
 *          keeps the canonical body tenant-agnostic.
 *
 * FLOW:
 *   ┌─────────────────────┐    ┌────────────────────┐    ┌─────────────────────┐
 *   │ React component +   │ →  │ axios via emailApi │ →  │ Quotech BE          │
 *   │ EmailListParams     │    │ (tenantId injected │    │ /api/email/*        │
 *   │ (folder/filters/    │    │  via setter)       │    │ + invalidation on   │
 *   │  search/page_size)  │    │                    │    │ mutation hooks      │
 *   └─────────────────────┘    └────────────────────┘    └─────────────────────┘
 *
 * DEPENDENCIES:
 *   - `@quotech/ui`: shared `EmailRecipient` type used by the row
 *     renderer and by InboxPage's bulk-select chip.
 *   - `../lib/api`: `emailApi` axios instance — tenant id is set via
 *     `setEmailTenantId(orgId)` in tenant boot. Do NOT import a
 *     tenant-specific axios from this file.
 *
 * RELATED:
 *   - apps/sam/src/features/email/hooks/useEmails.ts — shim
 *   - apps/newmark/src/features/email/hooks/useEmails.ts — shim
 *   - packages/email-hub/src/feature/pages/InboxPage.tsx — primary
 *     consumer; opts into `include_suggestions: true` for SuggestedJob
 *     rendering on unallocated rows.
 *   - packages/tenant-shared/src/email/api.ts — owns
 *     `EmailAllocationSummary` (extracted in MR-K4-partial to break
 *     the email-hub ↔ kanban-host circular dep).
 *   - docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md — full arc.
 *   - docs/architecture/EMAIL_KANBAN_CONSOLIDATION_STATUS.md —
 *     post-MR-H operating guide.
 *
 * EDIT HERE, NOT in apps/<tenant>/src/features/email/hooks/. The
 * shim-only CI gate (scripts/check-tenant-module-shim-only.py)
 * blocks any tenant feature file that contains anything other than
 * a re-export against this body.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getSearchRankParam } from '@quotech/ui'
import { emailApi, emailApiFetch } from '../lib/api'
import { navigateToOAuthProvider } from '@quotech/tenant-shared/lib/oauthNavigation'
import { notify } from '../../_internal/stores/notificationStore'
import { persistInboxList, readInboxList } from './useEmails.listCache'
import { persistEmailMeta, readEmailMeta } from './useEmails.metaCache'
import type {
  EmailRecipient, EmailAttachment, EmailExternalLink, QuoteRequestExtraction,
  EmailAssignment, EmailWatcher, EmailCalendarEvent, Email, SuggestedJob,
  EmailAllocationSummary, EmailStats, EmailListParams, EmailFacets, FilterPreset,
  EmailSyncStatusRow, EmailMailboxSyncStatus, EmailMailboxesSyncSummary,
  EmailSyncStatusResponse,
} from './useEmails.types'

// ARC EMAIL_HUB_LOADING_RESILIENCE phase 3 — all email-hub data-layer
// types were lifted into ./useEmails.types (zero behaviour change).
// Re-export them here so every existing
// `import { Email, EmailListParams, ... } from '.../useEmails'` path
// resolves byte-identically. The `import type` above binds the names
// for use inside the hook bodies below.
export * from './useEmails.types'

/** Single-shot fetch of /api/emails/sync/status/. The backfill walker
 *  uses this directly (no hook state) so it can ``await`` an outcome
 *  in line with the rest of its state machine. UI surfaces should
 *  use ``useEmailSyncStatus`` below.
 *
 *  ``since`` is forwarded as the ``?since=<ISO>`` query param so the
 *  caller can ask "did the sync I dispatched at <T> finish yet?"
 *  without false-positives from rows that finished before <T>.
 *
 *  ``opts.scope='org'`` requests the org-wide mailbox view (admin-only
 *  on the BE; silently downgraded to own+granted for non-admins). Used
 *  by the admin mailbox-health panel — everything else should poll the
 *  default own-scope. */
export async function fetchEmailSyncStatus(
  since?: string,
  opts?: { scope?: 'org' },
): Promise<EmailSyncStatusResponse | null> {
  try {
    const sp = new URLSearchParams()
    if (since) sp.set('since', since)
    if (opts?.scope) sp.set('scope', opts.scope)
    const url = sp.toString()
      ? `/api/emails/sync/status/?${sp.toString()}`
      : '/api/emails/sync/status/'
    // BUG-2026-06-22-EMAIL-AGGREGATE-IDENTITY-RELOAD-LOOP: the sync-status
    // poller (same cosmetic class as the already-migrated gmail/outlook status
    // pollers) must never arm the tab-wide identity reload. Opt out; genuine
    // drift is still caught by the data-bearing email reads + the banner.
    return await emailApi<EmailSyncStatusResponse>(url, { skipIdentityAssert: true })
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[fetchEmailSyncStatus] failed', err)
    }
    return null
  }
}

/** Categories that are safe to bulk-bin by sender. Promotional /
 *  bulk-blast mail only — deliberately EXCLUDES anything that could be
 *  a real subscription, receipt or operational notice (bill, invoice,
 *  payment, quote_request, general, …) so the "bin all similar" sweep
 *  can never nuke a sender's important mail just because one promo
 *  slipped through from the same address. */
export const BULK_BIN_CATEGORIES: ReadonlySet<string> = new Set([
  'marketing', 'promotion', 'promotions', 'newsletter', 'spam',
])

/** Collect inbox email IDs from a given sender that share a given
 *  category — backing the "bin all similar marketing emails from X"
 *  flow. Queries the LIST endpoint (not just the rows already on
 *  screen) so the sweep is retroactive across the whole mailbox. Caps
 *  at ``max`` ids (one lean page) which comfortably covers a sender's
 *  promo backlog; ``titles_only`` keeps the payload tiny. */
export async function fetchSimilarSenderEmailIds(
  fromAddress: string,
  category: string,
  opts: { excludeId?: string; max?: number } = {},
): Promise<string[]> {
  const addr = fromAddress?.trim()
  if (!addr || !category) return []
  const max = opts.max ?? 200
  const sp = new URLSearchParams()
  sp.set('from_address__icontains', addr)
  sp.set('category', category)
  sp.set('folder', 'inbox')
  sp.set('titles_only', 'true')
  sp.set('page_size', String(max))
  try {
    const data = await emailApi<
      { results?: Array<{ id: string }> } | Array<{ id: string }>
    >(`/api/emails/?${sp.toString()}`)
    const rows = Array.isArray(data) ? data : (data?.results ?? [])
    return rows
      .map((r) => String(r.id))
      .filter((id) => id && id !== opts.excludeId)
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[fetchSimilarSenderEmailIds] failed', err)
    }
    return []
  }
}

// List emails with filtering
export function useEmails(params: EmailListParams = {}) {
  // SWR instant-paint seed (operator: "seeing the list is taking time").
  // Only the PLAIN unfiltered inbox view is persisted/seeded — any filter
  // / search / non-inbox folder starts empty (cold) as before so a
  // filtered result never paints as the default inbox.
  const isDefaultInboxView =
    (params.folder === 'inbox' || params.folder === undefined) &&
    !params.category &&
    !params.category__in?.length &&
    !params.search &&
    !params.job_id &&
    !params.job_id__in?.length &&
    !params.allocation &&
    !params.from_address__icontains &&
    !params.company_domain &&
    !params.synced_by_user_id__in?.length &&
    !params.synced_by_account_id__in?.length &&
    !params.unattributed_owner_id__in?.length &&
    params.is_read === undefined &&
    params.is_starred === undefined &&
    params.has_attachments === undefined &&
    params.has_calendar_event === undefined &&
    !params.received_at__gte &&
    !params.received_at__lte &&
    // "Everything" scope (include_archived) is NOT the default inbox —
    // seeding it would flash swept rows into the clean Focus view.
    !params.include_archived
  // ``hide_cleared`` and ``category__not`` (the hide-promotions toggle)
  // participate in the seed KEY (not the gate) so each default-view
  // variant gets instant-paint seeding without ever painting the wrong
  // variant. Previously a category__not view was excluded from seeding
  // entirely, so operators with "hide promotions" on lost the instant
  // first paint every mount.
  const seedKey =
    params.persistKey && isDefaultInboxView
      ? `${params.persistKey}:inbox-default${params.hide_cleared ? ':cleared-hidden' : ''}${
          params.category__not?.length
            ? `:not-${[...params.category__not].sort().join('_')}`
            : ''
        }`
      : null
  // Read ONCE at mount (lazy initialiser) — later filter changes must not
  // re-seed mid-session.
  const [initialSeed] = useState<Email[] | null>(() =>
    seedKey ? readInboxList(seedKey) : null,
  )

  const [emails, setEmails] = useState<Email[]>(() => initialSeed ?? [])
  // Hardening (operator 2026-06-01: "make sure it doesn't fuck up updates
  // and new data"). The SWR seed is a PURE first-paint placeholder — it
  // must never contribute a row or a stale field to the live list. This
  // ref is true until the FIRST successful server response lands; that
  // response REPLACES the seed wholesale (see fetchEmails) so a row that
  // was deleted/binned/moved since the snapshot can never linger and no
  // stale seeded field survives. After it flips false the normal
  // enrichment-preserving merge resumes.
  const seedReplacePendingRef = useRef<boolean>(
    !!(initialSeed && initialSeed.length > 0),
  )
  // BUG-2026-06-23-EMAIL-FILTER-LOADMORE-STALE-TAIL — after the operator
  // clicked "Load older" (page 2+) and THEN changed the search / category /
  // mailbox query, the first Phase-1 response of the NEW query was merged
  // onto the OLD list via ``mergeRefreshedEmails``, whose tail-preservation
  // (rows older than the refreshed window) re-appended the previous filter's
  // page-2+ rows — so the new filter showed rows that don't match it. The
  // tail-merge is correct for a poll/refresh of the SAME query but wrong for
  // a query CHANGE. We track the queryString the live list was last built
  // for; the FIRST non-append response whose query differs REPLACES the list
  // wholesale (dropping the previous filter's tail) instead of tail-merging.
  // Poll / visibility / sync-queued refetches keep the same queryString, so
  // they still preserve the loaded tail. null = "no authoritative list yet".
  const lastFetchedQueryRef = useRef<string | null>(null)
  // Stack 13 MR-B — split the legacy ``loading`` flag into two
  // discrete states.
  //
  //   loadingInitial — true ONLY when there are no rows on screen
  //                    AND a fetch is in flight. This is the gate
  //                    behind the giant centred spinner in
  //                    InboxPage.tsx (line 1394). It flips FALSE
  //                    permanently as soon as the first
  //                    setEmails(...) call lands (even with [])
  //                    so the empty-state branch ("No emails yet")
  //                    becomes reachable. Critically — background
  //                    poll ticks do NOT flip this back to true,
  //                    so the perpetual spinner bug (10s poll vs
  //                    30s response stack-up) is structurally
  //                    impossible.
  //
  //   refreshing    — true whenever a fetch is in flight after the
  //                    initial load. Drives the small "Syncing…"
  //                    indicator next to the inbox header so the
  //                    operator can SEE that polling is happening
  //                    without losing visibility of the rows
  //                    underneath.
  //
  // The legacy ``loading`` field on the returned object is kept as
  // an alias for ``loadingInitial`` for backward compatibility
  // (every caller in the tree reads ``loading`` for the spinner
  // gate, which is the loadingInitial semantics). New callers should
  // prefer ``loadingInitial`` / ``refreshing`` directly.
  // When we have a seed, skip the cold centred spinner — paint the
  // persisted rows immediately and let the background revalidation
  // overwrite them in place.
  const [loadingInitial, setLoadingInitial] = useState(
    () => !(initialSeed && initialSeed.length > 0),
  )

  // Persist the default-inbox rows so the NEXT mount paints instantly.
  // Fires on every list change while on the unfiltered view (cheap
  // sessionStorage write); skips filtered/search views (seedKey === null)
  // so we never persist a filtered result as the default snapshot.
  //
  // Hardening: while the seed is still on screen (no authoritative fetch
  // yet) we DON'T re-persist it — otherwise a permanently-failing inbox
  // would keep refreshing the snapshot's timestamp and resurrect the same
  // stale rows forever. We only persist AFTER the first server response has
  // replaced the seed, so the cache only ever holds server-authoritative
  // data.
  useEffect(() => {
    if (!seedKey) return
    // Don't re-persist the placeholder before authoritative data lands.
    if (seedReplacePendingRef.current) return
    // Persist the authoritative set — INCLUDING an empty one. A stored
    // empty snapshot reads back as null (readInboxList treats [] as a
    // miss), so a mailbox that's been fully binned/emptied server-side
    // stops re-flashing stale seed rows on the next mount instead of
    // resurrecting them.
    persistInboxList(seedKey, emails)
  }, [seedKey, emails])
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextUrl, setNextUrl] = useState<string | null>(null)
  // BUG-2026-06-12-SEARCH-FALSE-EMPTY — which queryString has received
  // at least one SETTLED (non-aborted) response this session: success
  // OR a real error, either is a verdict for that query. ``loadingInitial``
  // flips false permanently after the session's first fetch, so when the
  // operator then typed a search the pane compared rowCount===0 +
  // loading===false and claimed "No emails match your filters" while the
  // search request was still in flight (operator-hit: a cold "heather"
  // FTS query took 7.2s server-side and the pane sat on a false
  // no-matches panel the whole time — looked like search was broken).
  // ``pendingNewQuery`` (returned below) is the honest "this exact
  // query hasn't answered yet" signal the host should treat as loading.
  // An aborted fetch deliberately does NOT settle — the replacement
  // fetch for the new queryString owns the verdict.
  const [settledQuery, setSettledQuery] = useState<string | null>(null)

  // EMAIL_HUB_COLD_START_RESILIENCE §B — cold-start fast-poll window.
  // While the list is EMPTY we tighten the visibility poll so the first
  // batch a fresh/empty mailbox's sync writes paints in ~4s instead of up
  // to the steady-state 10s. The window auto-closes after
  // COLD_START_WINDOW_MS so a genuinely-empty (no-mail) mailbox can't
  // hammer the list endpoint forever — it falls back to the normal cadence
  // and shows the honest "No emails yet" empty state. The moment ANY row
  // lands, ``emails.length > 0`` reverts the interval regardless of the
  // window. (We can't key this off the BE active_sync flag without
  // reordering the sync-status hook above this one; the empty+window gate
  // is reactive, self-contained, and bounded — which is all §B needs.)
  const COLD_START_POLL_MS = 4_000
  // Steady-state list poll. 30s hid mail that Gmail had already
  // written: the row sat in the database until the next paint.
  // 10s is the original ARC target. The inflight throttle below
  // still skips a tick when a fetch is already running.
  const NORMAL_POLL_MS = 10_000
  const COLD_START_WINDOW_MS = 120_000
  const [coldStartWindowOpen, setColdStartWindowOpen] = useState(true)
  useEffect(() => {
    const t = window.setTimeout(
      () => setColdStartWindowOpen(false),
      COLD_START_WINDOW_MS,
    )
    return () => window.clearTimeout(t)
  }, [])

  const queryString = useMemo(() => {
    const sp = new URLSearchParams()
    if (params.status) sp.set('status', params.status)
    if (params.category) sp.set('category', params.category)
    if (params.category__in?.length) {
      sp.set('category__in', params.category__in.join(','))
    }
    if (params.category__not?.length) {
      sp.set('category__not', params.category__not.join(','))
    }
    if (params.is_read !== undefined) sp.set('is_read', String(params.is_read))
    if (params.is_starred !== undefined) sp.set('is_starred', String(params.is_starred))
    if (params.job_id) sp.set('job_id', String(params.job_id))
    if (params.job_id__in?.length) sp.set('job_id__in', params.job_id__in.join(','))
    if (params.allocation) sp.set('allocation', params.allocation)
    if (params.hide_cleared) sp.set('hide_cleared', 'true')
    if (params.include_archived) sp.set('include_archived', 'true')
    if (params.needs_review) sp.set('needs_review', 'true')
    if (params.synced_by_user_id__in?.length) {
      sp.set('synced_by_user_id__in', params.synced_by_user_id__in.join(','))
    }
    if (params.synced_by_account_id__in?.length) {
      sp.set('synced_by_account_id__in', params.synced_by_account_id__in.join(','))
    }
    if (params.unattributed_owner_id__in?.length) {
      sp.set('unattributed_owner_id__in', params.unattributed_owner_id__in.join(','))
    }
    if (params.from_address__icontains) {
      sp.set('from_address__icontains', params.from_address__icontains)
    }
    if (params.company_domain) sp.set('company_domain', params.company_domain)
    if (params.received_at__gte) sp.set('received_at__gte', params.received_at__gte)
    if (params.received_at__lte) sp.set('received_at__lte', params.received_at__lte)
    if (params.has_attachments !== undefined) {
      sp.set('has_attachments', String(params.has_attachments))
    }
    if (params.has_calendar_event !== undefined) {
      sp.set('has_calendar_event', String(params.has_calendar_event))
    }
    if (params.folder) sp.set('folder', params.folder)
    if (params.attachment_filename) {
      sp.set('attachment_filename', params.attachment_filename)
    }
    if (params.search_scope && params.search) {
      // Scope only makes sense WITH a search term — never sent bare.
      sp.set('search_scope', params.search_scope)
    }
    if (params.search) {
      sp.set('search', params.search)
      // U1 (be!4448) — opt the inbox search into relevance ordering.
      // Only ever sent WITH a non-empty search; the backend pins
      // recency as the default for everything else. Kill switch:
      // localStorage qt-search-rank-disabled='1' (searchRankFlag in
      // @quotech/ui — shared with the palette email provider).
      // G3 — ``params.search_rank`` ('2' = Smart search hybrid
      // semantic) overrides the default when the host supplies it;
      // undefined keeps the fe!5642 behaviour bit-for-bit.
      const rank = params.search_rank ?? getSearchRankParam()
      if (rank) sp.set('rank', rank)
    }
    if (params.page_size) sp.set('page_size', String(params.page_size))
    if (params.include_suggestions) sp.set('include_suggestions', 'true')
    // Stack 13 MR-A — explicit single-phase override. The progressive
    // path below ADDS its own ``titles_only=true`` to a derived URL
    // (see ``fetchEmails(phase=...)``) so this flag is only consulted
    // when the caller wants a one-shot lean fetch.
    if (params.titles_only) sp.set('titles_only', 'true')
    return sp.toString()
    // Array deps are serialised to their joined string so a host
    // passing a fresh `[12, 15]` literal on every render doesn't
    // invalidate the memo (and re-trigger an inbox refetch) when
    // the contents haven't actually changed. Standard pattern for
    // primitive-equality dep tracking.
  }, [
    params.status,
    params.category,
    params.category__in?.join(','),
    params.category__not?.join(','),
    params.is_read,
    params.is_starred,
    params.job_id,
    params.job_id__in?.join(','),
    params.allocation,
    params.hide_cleared,
    params.include_archived,
    params.needs_review,
    params.synced_by_user_id__in?.join(','),
    params.synced_by_account_id__in?.join(','),
    params.unattributed_owner_id__in?.join(','),
    params.from_address__icontains,
    params.company_domain,
    params.received_at__gte,
    params.received_at__lte,
    params.has_attachments,
    params.has_calendar_event,
    params.folder,
    params.attachment_filename,
    params.search_scope,
    params.search,
    params.search_rank,
    params.page_size,
    params.include_suggestions,
    params.titles_only,
  ])

  // Stack 13 MR-B — inflight guard.
  //
  // Holds the AbortController for the currently-running fetch (or
  // null when idle). Used by two distinct paths:
  //
  //   1. POLLING THROTTLE — useVisiblePolling fires every 30s while
  //      the tab is visible. If a previous tick's fetch is still in
  //      flight (slow backend, network hiccup, OAuth refresh), we
  //      SKIP the new tick instead of stacking another in-flight
  //      request on top. This was the structural cause of the
  //      perpetual-spinner bug pre-MR-A (10s poll cadence + 30s BE
  //      response time + setLoading(true) on every tick).
  //
  //   2. FILTER-CHANGE CANCEL — when ``queryString`` changes (user
  //      picks a different category / search), the effect below
  //      .abort()s the running fetch FIRST so its eventual
  //      ``setEmails`` doesn't paint stale rows over the new
  //      filter's response. Without this, picking a category mid-
  //      fetch left the old category's rows on screen for the full
  //      response duration.
  const inflightRef = useRef<AbortController | null>(null)

  const fetchEmails = useCallback(
    async (
      append = false,
      url?: string,
      opts: { phase?: 'titles' | 'full' | 'legacy'; signal?: AbortSignal } = {},
    ) => {
      const phase = opts.phase ?? 'legacy'
      // Refresh-state semantics. Only the FIRST in-flight fetch flips
      // ``loadingInitial`` (the giant-spinner gate); every subsequent
      // fetch — including Phase 2 of the two-phase progressive flow
      // — flips ``refreshing`` instead. That's what keeps rows on
      // screen across poll ticks and across the Phase 1 → Phase 2
      // transition.
      setRefreshing(true)
      setError(null)
      try {
        const fetchUrl =
          url || `/api/emails/${queryString ? `?${queryString}` : ''}`
        // emailApi is the tenant-aware axios wrapper. It accepts a
        // standard fetch ``signal`` via its options bag — when the
        // signal aborts, the underlying request rejects and the
        // catch below treats it as a no-op (not an error toast).
        const data = await emailApi<any>(fetchUrl, { signal: opts.signal })

        if (import.meta.env.DEV) {
          console.debug('[useEmails] raw response:', {
            phase,
            type: typeof data,
            isArray: Array.isArray(data),
            isNull: data === null,
            keys:
              data && typeof data === 'object' ? Object.keys(data) : 'N/A',
            preview: JSON.stringify(data)?.slice(0, 500),
          })
        }

        let results: any[] = []
        if (Array.isArray(data)) {
          results = data
        } else if (data && typeof data === 'object') {
          const candidate =
            data.results ?? data.emails ?? data.data ?? data.items ?? []
          results = Array.isArray(candidate) ? candidate : []
        }

        if (!append && seedReplacePendingRef.current) {
          // Hardening — first authoritative response after an SWR seed.
          // REPLACE the placeholder wholesale (no field-merge from the
          // seed) so the server response is the single source of truth:
          //   - a row deleted / binned / moved since the snapshot is
          //     absent from ``results`` and therefore gone from the list;
          //   - no stale seeded field (read-state, category, allocation,
          //     company, …) can survive into a live row;
          //   - genuinely new mail in ``results`` shows immediately.
          // From here the seed is discarded and the normal
          // enrichment-preserving merge resumes for Phase 2 + polls. This
          // makes a seeded load behave EXACTLY like a cold load after the
          // first response — the seed only ever bought us the instant
          // first paint, never the data.
          seedReplacePendingRef.current = false
          lastFetchedQueryRef.current = queryString
          setEmails(
            results.filter(
              (r: any): r is Email =>
                r && typeof r === 'object' && typeof r.id === 'string',
            ),
          )
        } else if (phase === 'full' && !append) {
          // Stack 13 MR-B — enrichment merge.
          //
          // Phase 2 lands with the SAME page set as Phase 1 (cursor
          // pagination is stable across the back-to-back fetches),
          // but each row now carries the full chip payload
          // (allocation_summary, suggested_job, on_card, etc.). We
          // merge by id rather than replace so:
          //
          //   - Rows already on screen don't unmount + remount (no
          //     selection flash, no scroll jump).
          //   - Any row the user CLICKED between Phase 1 and Phase 2
          //     keeps its selection (selectedEmailId is by id, the
          //     row object is replaced in place via the React key).
          //   - If Phase 2 returns MORE rows than Phase 1 (rare —
          //     someone synced new mail in the 200 ms gap), we still
          //     append them at the bottom rather than losing them.
          //
          // ``setEmails`` always returns a NEW array so React's
          // reconciler picks up the per-row prop changes. The merge
          // is O(n + m); fine for the 200-row inbox page cap.
          setEmails(prev => {
            const enriched = new Map<string, Email>()
            for (const row of results) {
              if (row && typeof row === 'object' && typeof row.id === 'string') {
                enriched.set(row.id, row as Email)
              }
            }
            const out: Email[] = []
            const seen = new Set<string>()
            for (const row of prev) {
              if (row && typeof row.id === 'string') {
                const enrichedRow = enriched.get(row.id)
                out.push(enrichedRow ?? row)
                seen.add(row.id)
              }
            }
            // Append any Phase-2-only rows the user hasn't seen yet.
            for (const [id, row] of enriched.entries()) {
              if (!seen.has(id)) out.push(row)
            }
            return out
          })
        } else if (append) {
          // Dedupe by id — after a poll refresh preserved the loaded
          // tail (see mergeRefreshedEmails), the next-page cursor can
          // overlap rows already on screen. Duplicate ids would crash
          // React's keyed reconciliation expectations (and look mad).
          setEmails(prev => {
            const seen = new Set(prev.map(r => r?.id).filter(Boolean))
            const fresh = results.filter(
              (r: any) => r && typeof r.id === 'string' && !seen.has(r.id),
            )
            return fresh.length > 0 ? [...prev, ...fresh] : prev
          })
        } else {
          // BUG-2026-05-24-EMAIL-CHIP-FLICKER-ON-POLL-REFETCH. Refresh /
          // poll / Phase-1-titles_only rows are a strict subset of the
          // Phase-2 / full-fetch enriched rows the FE chips depend on.
          // A naive `setEmails(results)` here wipes `mailboxes`,
          // `synced_by_email`, `synced_by_name`, `allocation_summary`,
          // `on_card_*`, `suggested_job`, etc. for ~200-400 ms on
          // every 10-s poll tick → operator sees chips flicker
          // `Mark` → `?`, kanban-card badges (`#211 ✓`) blink, category
          // chips flash empty. Pure I→O merge below; see test pin
          // `useEmails.flicker.test.ts` for the contract.
          //
          // BUG-2026-06-23: on the FIRST response of a CHANGED query, replace
          // wholesale instead of tail-merging — otherwise the previous
          // filter's loaded page-2+ tail (older than this query's window)
          // leaks back in. A poll/refresh of the SAME query keeps the
          // queryString, so it still tail-merges (preserving "Load older").
          const queryChanged = lastFetchedQueryRef.current !== queryString
          lastFetchedQueryRef.current = queryString
          if (queryChanged) {
            setEmails(
              results.filter(
                (r: any): r is Email =>
                  r && typeof r === 'object' && typeof r.id === 'string',
              ),
            )
          } else {
            setEmails(prev => mergeRefreshedEmails(prev, results as Email[]))
          }
        }

        // Email→Quote ARC § 3.3 (P3) — populate the module-scope cache
        // so a subsequent ``useEmail(<id>)`` for any of these rows can
        // seed its initial render synchronously. The seed pays back the
        // operator complaint that the right pane goes blank on every
        // inbox click; with the cache populated the EmailReader paints
        // header + sender + tags in the same frame and the body fetch
        // refines the row in place.
        //
        // Phase 1 (titles_only) and Phase 2 (full) both write here —
        // Phase 2's payload wins because it's a superset. The
        // delete-then-set inside ``rememberEmailSummary`` refreshes
        // the LRU position so frequently-touched rows survive
        // eviction.
        for (const row of results) {
          if (row && typeof row === 'object' && typeof row.id === 'string') {
            rememberEmailSummary(row as Email)
          }
        }
        const next =
          data && typeof data === 'object' && !Array.isArray(data)
            ? data.next ?? null
            : null
        setNextUrl(next)
        setHasMore(!!next)
        // This query has a server verdict — the closed-over queryString
        // is the one this fetch was issued for (useCallback dep), so a
        // late response from a superseded query can never settle the
        // current one. See BUG-2026-06-12-SEARCH-FALSE-EMPTY.
        setSettledQuery(queryString)

        if (import.meta.env.DEV && results.length === 0) {
          console.warn(
            '[useEmails] parsed 0 results from API. Raw keys:',
            data && typeof data === 'object' ? Object.keys(data) : typeof data,
          )
        }
      } catch (err: any) {
        // Aborted fetches are EXPECTED whenever the user changes
        // filters mid-flight or navigates away. They are NOT
        // surfaced as toast-able errors and they do NOT clear the
        // current row set (the replacement fetch will land
        // shortly). Both axios and native fetch use the standard
        // AbortError name + signal.aborted flag — we cover both
        // surfaces because the email-hub axios wrapper may have
        // adapter differences across tenants.
        //
        // BUG-2026-05-23-EMAIL-STUCK-LOADING: the client-side fetch
        // timeout in quotechFetch throws a QuotechFetchTimeoutError
        // (NOT an AbortError) precisely so it falls through the
        // isAbort no-op branch into the visible error surface below.
        // Callers that legitimately cancel (filter change, unmount)
        // see the original AbortError shape and are silently dropped;
        // structural hangs (silent-refresh poison-pill, half-closed
        // proxy, stalled TCP) surface as a typed error with a
        // user-facing retry message.
        const isAbort =
          opts.signal?.aborted ||
          (err && (err.name === 'AbortError' || err.name === 'CanceledError'))
        if (isAbort) {
          if (import.meta.env.DEV) {
            console.debug('[useEmails] fetch aborted (filter change or unmount)')
          }
          return
        }
        // Prefer the typed user-facing message when present so the
        // operator sees "Request timed out — please try again." rather
        // than the debug-shaped "Request to /api/emails/... timed out
        // after 25000ms".
        const friendly =
          err && typeof err === 'object' && typeof (err as { userMessage?: unknown }).userMessage === 'string'
            ? (err as { userMessage: string }).userMessage
            : null
        const msg = friendly ?? (err instanceof Error ? err.message : 'Failed to fetch emails')
        if (import.meta.env.DEV) console.error('[useEmails] fetch error:', msg, err)
        setError(msg)
        // A real failure is also a verdict — settle so the pane shows
        // the error/empty surface instead of an indefinite skeleton.
        setSettledQuery(queryString)
      } finally {
        // Always flip ``loadingInitial`` false on the first response —
        // the empty-state branch in InboxPage is then reachable.
        // ``refreshing`` flips false unconditionally on every fetch
        // completion (legacy spinner UX preserved for the small
        // "Syncing…" badge).
        setLoadingInitial(false)
        setRefreshing(false)
      }
    },
    [queryString],
  )

  // Stack 13 MR-B — the orchestrator.
  //
  // Default flow (progressive !== false):
  //   1. Phase 1 — fire ?titles_only=true (≈200 ms). Paints subjects
  //      + sender + preview + read-state immediately. Behind a
  //      single AbortController so a filter change cancels both
  //      phases in lockstep.
  //   2. Phase 2 — fire the SAME query string WITHOUT titles_only.
  //      Lands ≈400 ms after Phase 1 with the full chip payload.
  //      Merged into the existing rows by id (see ``fetchEmails``
  //      above). The merge means no list re-mount, no scroll jump,
  //      no selection flash.
  //
  // Opt-out (progressive === false): a single full fetch only —
  // legacy behaviour for surfaces that need chips on first paint.
  //
  // The ``url`` param is set by the "Load more" pagination button
  // and bypasses the orchestrator entirely (legacy append path).
  const runEmailsFetch = useCallback(
    async (append = false, url?: string) => {
      // Cancel any in-flight prior fetch — see ``inflightRef`` doc
      // for the two failure modes this guards against.
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
      const controller = new AbortController()
      inflightRef.current = controller
      try {
        if (url || append || params.progressive === false || params.titles_only) {
          // Legacy single-phase path. Append (pagination), explicit
          // URL (next-page), or caller-opt-out goes through here.
          await fetchEmails(append, url, {
            phase: 'legacy',
            signal: controller.signal,
          })
        } else {
          // Stack 13 MR-B — two-phase progressive flow.
          // Phase 1 fires synchronously; Phase 2 is awaited too so
          // the inflightRef can be cleared cleanly at the end.
          //
          // Stack 13 MR-A-3 (2026-05-20) — strip ``include_suggestions``
          // from the Phase 1 URL. The BE skips suggestion scoring on
          // the lean path anyway (see ``_wants_titles_only`` gate in
          // ``EmailListView.list``) so the param had no functional
          // effect, but the FE was sending it as a side-effect of
          // sharing one ``queryString`` memo across both phases.
          // Dropping it on Phase 1 makes the wire contract honest
          // (Phase 1 = no chips, no suggestions, no allocations →
          // ?titles_only=true alone says exactly that) and removes
          // the dead URL parameter from the network panel during
          // operator debugging.
          const phase1Params = new URLSearchParams(queryString)
          phase1Params.delete('include_suggestions')
          phase1Params.set('titles_only', 'true')
          const phase1Url = `/api/emails/?${phase1Params.toString()}`
          await fetchEmails(false, phase1Url, {
            phase: 'titles',
            signal: controller.signal,
          })
          if (controller.signal.aborted) return
          // Yield after Phase 1 so React can commit titles rows (and the
          // browser can paint) before Phase 2 contends for the same
          // connection pool / main-thread JSON parse. Sequential order
          // is preserved — we only insert one frame of breathing room.
          await new Promise<void>((resolve) => {
            if (
              typeof window !== 'undefined'
              && typeof window.requestAnimationFrame === 'function'
            ) {
              window.requestAnimationFrame(() => resolve())
            } else {
              setTimeout(resolve, 0)
            }
          })
          if (controller.signal.aborted) return
          await fetchEmails(false, undefined, {
            phase: 'full',
            signal: controller.signal,
          })
        }
      } finally {
        if (inflightRef.current === controller) {
          inflightRef.current = null
        }
      }
    },
    [fetchEmails, queryString, params.progressive, params.titles_only],
  )

  useEffect(() => {
    runEmailsFetch()
    // The cleanup .abort() is what stops a stale fetch from leaking
    // its setEmails into the freshly-mounted page after a filter
    // change. The cleanup runs synchronously BEFORE the new
    // runEmailsFetch starts, so the timeline is: cleanup-abort →
    // controller.signal.aborted=true → in-flight emailApi rejects
    // → fetchEmails catch branch noops → effect re-runs with the
    // new queryString.
    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [runEmailsFetch])

  // Visibility-aware polling: while the tab is foreground, refetch the inbox
  // every 10s so new mail picked up by the backend Celery beat (*/2) or
  // webhook consumer (30s) shows up without the user navigating away.
  //
  // Cadence pinned per Email→Quote ARC § 3.2 + core/settings/base.py
  // comment block (2026-05-19): combined BE */2 + FE */10s targets
  // <30s mailbox→painted-row p50. Stack 13 MR-B briefly raised this
  // to 30s when the BE was slow; MR-A fixed the BE latency, so 10s
  // is safe again with the inflight-throttle below.
  //
  // EMAIL_HUB_COLD_START_RESILIENCE §B — drop to COLD_START_POLL_MS while
  // the list is empty inside the cold-start window so a fresh mailbox's
  // first batch paints fast; revert to NORMAL_POLL_MS the instant rows
  // land (or the window closes). useVisiblePolling re-installs its timer
  // whenever this value changes (effect dep on intervalMs).
  // The fast cadence only applies to the DEFAULT inbox view: a filter /
  // search that legitimately matches nothing used to trip the
  // empty-list condition and hammer the endpoint at 4s for the whole
  // 120s window.
  const pollIntervalMs =
    emails.length === 0 && coldStartWindowOpen && isDefaultInboxView
      ? COLD_START_POLL_MS
      : NORMAL_POLL_MS
  useVisiblePolling(() => {
    // Inflight-throttle. Skip the poll tick if a fetch is already
    // running — see ``inflightRef`` doc for the perpetual-spinner
    // failure mode this guards against. Phase 2 of the progressive
    // path counts as "in flight" too, so a Phase-2-still-running
    // state correctly suppresses an overlapping poll tick.
    if (inflightRef.current) {
      if (import.meta.env.DEV) {
        console.debug('[useEmails] poll tick skipped - fetch in flight')
      }
      return
    }
    runEmailsFetch()
  }, pollIntervalMs)

  // When a sync gets queued (Quotech-backend dispatched to Celery and
  // returned 202), the actual emails arrive in the database a few
  // seconds later. Refetch on a short delay so the inbox feels snappy
  // without waiting up to 60s for the next polling tick.
  useEffect(() => {
    // 6s and 15s — covers fast (small chunk) and slow (initial pull
    // backed by Graph rate-limiting) workers without piling on.
    // Routed through ``runEmailsFetch`` (not bare ``fetchEmails``)
    // so the inflight-throttle + progressive two-phase orchestration
    // still applies — without that the queued-refetch could land
    // on top of an in-flight poll tick and re-trigger the perpetual-
    // spinner failure mode the throttle is there to prevent.
    //
    // Timer ids live in a Set that the cleanup clears — the old
    // version stashed them as properties on the handler function and
    // never cancelled them, so a queued sync right before unmount /
    // filter change fired runEmailsFetch into a dead closure.
    const timers = new Set<number>()
    const onQueued = () => {
      const t1 = window.setTimeout(() => { timers.delete(t1); runEmailsFetch() }, 6_000)
      const t2 = window.setTimeout(() => { timers.delete(t2); runEmailsFetch() }, 15_000)
      timers.add(t1)
      timers.add(t2)
    }
    window.addEventListener(EMAIL_SYNC_QUEUED_EVENT, onQueued as EventListener)
    return () => {
      for (const t of timers) window.clearTimeout(t)
      timers.clear()
      window.removeEventListener(EMAIL_SYNC_QUEUED_EVENT, onQueued as EventListener)
    }
  }, [runEmailsFetch])

  // Stack 13 MR-D — listen for the BE-side "sync just finished"
  // signal emitted by ``useEmailSyncStatus`` and trigger an
  // immediate inbox refresh. The 30s background poll is the
  // fallback for the rare case where the sync-status polling
  // misses the transition (60s idle window happened to straddle
  // a sync completion that finished + got polled past).
  //
  // Why a 750ms debounce:
  //   - The BE writes the ``EmailSyncLog.finished_at`` row a
  //     fraction of a second BEFORE the underlying Email rows are
  //     committed (different transactions; the log writer commits
  //     first). Firing the refetch the same tick we see
  //     ``finished_at`` advance can race against the email
  //     visibility predicate — caught the row not yet committed,
  //     paint identical results, look broken.
  //   - 750ms is empirically the p99 BE commit lag observed in
  //     prod (read by hand-watching journalctl during the Stack
  //     13 MR-A smoke).
  //   - We do NOT use a longer debounce (e.g. 3s) because that
  //     would defeat the whole point — sub-10s freshness is the
  //     headline UX win.
  //
  // The inflight throttle on ``runEmailsFetch`` makes overlapping
  // invalidations safe: if a 750ms-debounced refetch is fired
  // while a poll-tick refetch is already running, the new fetch
  // is aborted + replaced (filter-change-cancel path).
  useEffect(() => {
    let debounceTimer: number | null = null
    const onInvalidate = () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer)
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null
        runEmailsFetch()
      }, 750)
    }
    window.addEventListener(
      EMAIL_LIST_INVALIDATE_EVENT,
      onInvalidate as EventListener,
    )
    return () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer)
        debounceTimer = null
      }
      window.removeEventListener(
        EMAIL_LIST_INVALIDATE_EVENT,
        onInvalidate as EventListener,
      )
    }
  }, [runEmailsFetch])

  const loadMore = useCallback(() => {
    // Use ``loadingInitial`` (not the legacy combined ``loading``)
    // as the lockout — load-more is allowed to fire DURING a
    // background poll refresh; it just can't fire before the
    // initial page has settled.
    if (!loadingInitial && hasMore && nextUrl) runEmailsFetch(true, nextUrl)
  }, [loadingInitial, hasMore, nextUrl, runEmailsFetch])

  // Backwards-compat ``loading`` alias — every existing consumer
  // (InboxPage's giant spinner gate, StarredPage, SentPage, etc.)
  // reads ``loading`` for the "no rows yet, show spinner" decision.
  // That's the loadingInitial semantics. New consumers should prefer
  // ``loadingInitial`` / ``refreshing`` directly.
  // Optimistic local row patch — lets the host paint a state change
  // (read dot, star) in the SAME frame as the click instead of waiting
  // for the next poll tick to confirm the server write (which left rows
  // visually unread for up to 30s).
  const updateEmailLocal = useCallback((id: string, patch: Partial<Email>) => {
    setEmails(prev => {
      let changed = false
      const next = prev.map(e => {
        if (e.id !== id) return e
        changed = true
        return { ...e, ...patch }
      })
      return changed ? next : prev
    })
  }, [])

  // Optimistically drop actioned rows from the visible list so a Done /
  // Junk disposition CLEARS instantly instead of lingering until the
  // server round-trip refetch lands (operator 2026-06-14: "you click
  // done and then it clears up emails — you shouldn't see emails you
  // have already actioned here"). Safe to pair with refetch: the BE now
  // excludes archived rows from the inbox folder, so a racing refetch
  // can't paint the cleared row back.
  const removeEmailsLocal = useCallback((ids: string[]) => {
    if (ids.length === 0) return
    const drop = new Set(ids)
    setEmails(prev => {
      const next = prev.filter(e => !drop.has(e.id))
      return next.length === prev.length ? prev : next
    })
  }, [])

  // Inverse of removeEmailsLocal for the reader's terminal-action rollback
  // (change #269): reinsert rows at their ORIGINAL positions when a Done /
  // Archive / Junk / Delete server write fails after the optimistic removal,
  // or when the operator presses Undo. Indices are clamped; an id already
  // present (e.g. a racing refetch repainted it) is skipped.
  const restoreEmailsLocal = useCallback(
    (entries: Array<{ email: Email; index: number }>) => {
      if (entries.length === 0) return
      setEmails(prev => {
        const next = prev.slice()
        let changed = false
        for (const { email, index } of entries) {
          if (next.some(e => e.id === email.id)) continue
          next.splice(Math.max(0, Math.min(index, next.length)), 0, email)
          changed = true
        }
        return changed ? next : prev
      })
    },
    [],
  )

  return {
    emails,
    loading: loadingInitial,
    loadingInitial,
    refreshing,
    // True until the CURRENT queryString has received a settled
    // (non-aborted) response this session. Hosts must treat this as
    // "loading" for empty-state decisions — see
    // BUG-2026-06-12-SEARCH-FALSE-EMPTY at the settledQuery decl.
    pendingNewQuery: settledQuery !== queryString,
    error,
    hasMore,
    loadMore,
    refetch: () => runEmailsFetch(),
    updateEmailLocal,
    removeEmailsLocal,
    restoreEmailsLocal,
  }
}


// ARC EMAIL_HUB_LOADING_RESILIENCE phase 3 — the visibility-gated polling
// hook (useVisiblePolling) was lifted into useEmails.polling.ts (zero
// behaviour change). It stays module-private here (imported, not
// re-exported); the call site lives in the inbox list hook above.
import { useVisiblePolling } from './useEmails.polling'


// ARC EMAIL_HUB_LOADING_RESILIENCE phase 3 — the pure email-list summary
// LRU cache + the chip-preserving refresh merge were lifted into sibling
// modules (zero behaviour change). The hooks below import them for use;
// the public surface is re-exported so existing
// `import { mergeRefreshedEmails, resetEmailListCache,
//          __resetEmailListCacheForTests } from '.../useEmails'`
// paths stay byte-identical.
import { mergeDetailOverSeed, mergeRefreshedEmails } from './useEmails.merge'
import {
  rememberEmailSummary,
  getCachedEmailSummary,
  resetEmailListCache,
  __resetEmailListCacheForTests,
} from './useEmails.cache'

export {
  mergeDetailOverSeed,
  mergeRefreshedEmails,
  resetEmailListCache,
  __resetEmailListCacheForTests,
}


// ARC EMAIL_HUB_LOADING_RESILIENCE phase 3 — the Stack-13-MR-C body-prefetch
// cascade subsystem (queue class + module singleton + the reactive
// useEmailBodyPrefetch hook) was lifted into useEmails.bodyPrefetch.ts
// (zero behaviour change). Re-exported here so existing
// `import { useEmailBodyPrefetch } from '.../useEmails'` paths stay
// byte-identical.
export {
  useEmailBodyPrefetch,
  __resetBodyPrefetchQueueForTests,
  __getBodyPrefetchQueueStateForTests,
} from './useEmails.bodyPrefetch'
export type { UseEmailBodyPrefetchOptions } from './useEmails.bodyPrefetch'

// Single email by ID
export function useEmail(emailId: string | null) {
  // Seed initial state from the list cache so the EmailReader can
  // paint header/sender/tags/preview *synchronously* on click — see
  // Email→Quote ARC § 3.3 (P3) for the rationale and the
  // commit-then-fill rendering contract. When no cache hit exists
  // (cold mount, direct-link to /email/:id, etc.) we fall back to
  // the previous "null + spinner" behaviour.
  const seed = getCachedEmailSummary(emailId)
  const [email, setEmail] = useState<Email | null>(seed)
  // ``loading`` retains its original meaning: "there is NO row to
  // render yet". When we seeded from the cache, we have a row to
  // render even though the detail GET is still in flight, so
  // ``loading`` is false from the start. Body-only refinement is
  // signalled via ``bodyLoading`` so the EmailReader can decide
  // whether to render the body skeleton vs the real body without
  // also tearing down the header. (loading→true happens only when
  // emailId is set AND we have no cached summary AND the detail
  // fetch hasn't resolved yet.)
  const [loading, setLoading] = useState(seed === null && emailId !== null)
  const [bodyLoading, setBodyLoading] = useState(emailId !== null)
  const [error, setError] = useState<string | null>(null)

  // BUG-2026-06-11-DETAIL-FETCH-RACE: rapid j/k row-hopping used to
  // leave every prior detail GET running; whichever response landed
  // LAST painted, so the reader could show email A's body under email
  // B's header. One controller per fetch — superseded fetches are
  // aborted and their (stale) responses dropped.
  const detailAbortRef = useRef<AbortController | null>(null)

  const fetchEmail = useCallback(async () => {
    if (!emailId) {
      detailAbortRef.current?.abort()
      detailAbortRef.current = null
      setEmail(null)
      setLoading(false)
      setBodyLoading(false)
      return
    }
    detailAbortRef.current?.abort()
    const controller = new AbortController()
    detailAbortRef.current = controller
    // Re-seed on id-change so a second click on a different cached
    // row paints instantly instead of carrying over the previous
    // row's render. This is a no-op when the cache is empty.
    const reSeed = getCachedEmailSummary(emailId)
    if (reSeed) {
      setEmail(reSeed)
      setLoading(false)
    } else {
      setLoading(true)
    }
    setBodyLoading(true)
    setError(null)
    try {
      const result = await emailApi<Email>(`/api/emails/${emailId}/`, {
        signal: controller.signal,
      })
      if (controller.signal.aborted) return
      // Merge, never replace: the detail row does NOT carry strictly more
      // than the list row (see mergeDetailOverSeed). ``reSeed`` is the row we
      // painted from; re-read the cache in case a poll refreshed it mid-GET.
      const merged = mergeDetailOverSeed(
        reSeed ?? getCachedEmailSummary(emailId),
        result,
      )
      setEmail(merged)
      rememberEmailSummary(merged)
    } catch (err) {
      // Superseded/unmounted fetches are expected — not an error state.
      if (
        controller.signal.aborted ||
        (err instanceof Error && (err.name === 'AbortError' || err.name === 'CanceledError'))
      ) {
        return
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch email')
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false)
        setBodyLoading(false)
      }
      if (detailAbortRef.current === controller) detailAbortRef.current = null
    }
  }, [emailId])

  useEffect(() => {
    fetchEmail()
    return () => {
      detailAbortRef.current?.abort()
      detailAbortRef.current = null
    }
  }, [fetchEmail])

  // Email→Quote ARC § Phase 5 / Scope 2A — light visible-tab polling
  // while ANY external-share-link download is non-terminal so the
  // chip's %-progress bar advances without a websocket. The
  // poll suppresses itself the moment every link is terminal
  // (`done` / `failed` / `skipped`), so a fully-downloaded inbox
  // pays zero ongoing network cost.
  //
  // We deliberately compute `hasInflight` from the LATEST email
  // snapshot (not memoised across renders) so the loop reacts to
  // the very next poll's response — a one-poll lag between "done"
  // and "stop polling" is acceptable; a stale memo would burn a
  // permanent 3s tick on a finished email.
  //
  // `document.hidden` gates the tick the same way the InboxPage
  // visible-poll does (P2 / Scope 1A) — backgrounding the tab
  // mutes the chip-poll until the operator returns.
  const hasInflightDownload =
    Array.isArray(email?.external_links) &&
    email!.external_links!.some(
      (l) => l.download_status === 'pending' || l.download_status === 'downloading',
    )
  // Email→Quote ARC § Phase 8 — same poll-when-in-flight pattern
  // for the LLM extractor + Phase 9 cascade. The extractor takes
  // 5–15s typically (LLM call + folder cascade), so a 3s poll
  // surfaces the address + scope block as soon as it lands without
  // making the operator refresh.
  const hasInflightExtraction =
    !!email?.quote_request_extraction &&
    (email.quote_request_extraction.status === 'pending' ||
      email.quote_request_extraction.status === 'extracting')
  // Email→Quote ARC § Phase 11 — same pattern for the draft-reply
  // generator. The draft is a SEPARATE Celery task that runs in
  // parallel with the P9 QDrive cascade, so its lifecycle is
  // independent of the extraction's status — keep polling until
  // the draft itself transitions to a terminal state (ready /
  // failed / used).
  const hasInflightDraftReply =
    !!email?.quote_request_extraction &&
    (email.quote_request_extraction.draft_reply_status === 'pending' ||
      email.quote_request_extraction.draft_reply_status === 'generating')
  const shouldPoll =
    hasInflightDownload || hasInflightExtraction || hasInflightDraftReply
  useEffect(() => {
    if (!emailId) return
    if (!shouldPoll) return
    const POLL_MS = 3000
    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchEmail()
    }
    const id = window.setInterval(tick, POLL_MS)
    return () => window.clearInterval(id)
  }, [emailId, shouldPoll, fetchEmail])

  const patchLocal = useCallback((patch: Partial<Email>) => {
    setEmail((prev) => {
      if (!prev) return prev
      const next = { ...prev, ...patch }
      rememberEmailSummary(next)
      return next
    })
  }, [])

  return { email, loading, bodyLoading, error, refetch: fetchEmail, patchLocal }
}

// Email thread
export function useEmailThread(emailId: string | null) {
  const [thread, setThread] = useState<Email[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // BUG-2026-06-15-EMAIL-THREAD-APPENDS-INSTEAD-OF-REPLACING — operator: open
  // email 1 (a thread), then click email 2 → email 1 stays and email 2 is
  // appended below it; after that only the tail slot updates. Root cause: the
  // previously-loaded `thread` survived across selections, and the render
  // merged it with the freshly-opened email (appending when the new id wasn't
  // in the stale list). Reset synchronously the moment the selected email id
  // changes so a thread from a *different* email can never merge with the new
  // one. The fetch below then repopulates for the new selection.
  //
  // BUG-2026-06-23-EMAIL-THREAD-FETCH-RACE — the reset above stopped the
  // *append*, but a slow `/thread/` response for the PREVIOUSLY-selected
  // email could still resolve AFTER the operator clicked a different one and
  // call `setThread` with the wrong conversation (no AbortController / request
  // -id guard). `reqSeqRef` is bumped on every selection change AND at the top
  // of each fetch; a resolved fetch only writes state when its captured seq is
  // still the latest, so a stale in-flight thread can never paint under the
  // new header.
  const reqSeqRef = useRef(0)
  useEffect(() => {
    reqSeqRef.current += 1
    setThread([])
    setError(null)
  }, [emailId])

  const fetchThread = useCallback(async () => {
    if (!emailId) { setThread([]); return }
    const seq = ++reqSeqRef.current
    setLoading(true)
    setError(null)
    try {
      const result = await emailApi<{ emails: Email[] }>(`/api/emails/${emailId}/thread/`)
      if (seq !== reqSeqRef.current) return
      setThread(result.emails || [])
    } catch (err) {
      if (seq !== reqSeqRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to fetch thread')
    } finally {
      if (seq === reqSeqRef.current) setLoading(false)
    }
  }, [emailId])

  return { thread, loading, error, fetchThread }
}

// ARC EMAIL_HUB_LOADING_RESILIENCE phase 3 step 5 — the process-wide
// single-flight sync subsystem (doSync + the g_sync* module-scope mutable
// state + the sync pub-sub + useSyncEmails + the EMAIL_SYNC_* event channels)
// was lifted into useEmails.sync.ts. It is the one stateful subsystem, so ALL
// of its mutable state and every reader/writer live together in that single
// module (one shared instance; no cross-module binding reassignment).
// Re-exported here so existing `import { useSyncEmails, setSyncQueuedWaiting,
// EMAIL_SYNC_QUEUED_EVENT, EMAIL_SYNC_AUTH_FAILED_EVENT } from '.../useEmails'`
// paths stay byte-identical. The inbox/stats hooks below import
// EMAIL_SYNC_QUEUED_EVENT from here.
import {
  EMAIL_SYNC_AUTH_FAILED_EVENT,
  EMAIL_SYNC_QUEUED_EVENT,
  setSyncQueuedWaiting,
  useSyncEmails,
} from './useEmails.sync'

export {
  EMAIL_SYNC_AUTH_FAILED_EVENT,
  EMAIL_SYNC_QUEUED_EVENT,
  setSyncQueuedWaiting,
  useSyncEmails,
}

/** Stack 13 MR-D — fired whenever ``useEmailSyncStatus`` observes
 *  the backend's ``latest.finished_at`` advance (i.e. a sync just
 *  completed — manual click OR Celery beat OR external dispatch).
 *  ``useEmails`` listens for it and triggers an immediate inbox
 *  refetch so new mail appears within ~8s of the BE finishing,
 *  rather than waiting up to 30s for the next polling tick.
 *
 *  Pairs with the existing ``EMAIL_SYNC_QUEUED_EVENT`` (which is
 *  the "I just sent the request" signal) — together they cover
 *  both ends of the sync lifecycle:
 *
 *    user click  →  EMAIL_SYNC_QUEUED_EVENT       →  6s + 15s ladder refetch
 *    sync done   →  EMAIL_LIST_INVALIDATE_EVENT   →  immediate refetch
 *    no events   →  30s background poll fallback  →  catches everything else
 *
 *  Why a CustomEvent + module-scope sync-status hook, not a true
 *  SSE / EventSource:
 *
 *    - True SSE needs new BE plumbing (a long-lived ``/api/emails/
 *      events/`` stream, gunicorn-gevent connection accounting).
 *    - Microsoft Graph + Gmail Pub/Sub webhooks (the actual
 *      push-source of new mail) are NOT registered yet on prod
 *      (see PRODUCT_STATE_AND_SALES_READINESS.md — the
 *      ``email_webhook_events`` table has 0 rows). Until they
 *      are, an SSE stream would still poll the BE internally
 *      every few seconds — same effective latency, more code.
 *    - ``useEmailSyncStatus`` ALREADY adaptively polls
 *      ``/api/emails/sync/status/`` (8s during active sync,
 *      60s idle). Plumbing its existing signal into a list
 *      invalidation gives us sub-10s freshness during sync
 *      windows with ZERO new infrastructure.
 *
 *  Future MR-E (when webhooks are registered) can swap the
 *  CustomEvent source from sync-status polling to a true SSE
 *  push without changing the inbox-side listener — the event
 *  contract here is the boundary.
 *
 *  ``detail`` payload (optional): ``{ since: ISO-8601 string }``
 *  is the ``finished_at`` of the sync that triggered the
 *  invalidation. Listeners that want to debounce overlapping
 *  invalidations can compare it against a last-seen ref. */
export const EMAIL_LIST_INVALIDATE_EVENT = 'email-list-invalidate'


// ARC EMAIL_HUB_LOADING_RESILIENCE phase 3 — the optimistic stats-delta
// cluster (event + EmailStatsDelta shape + dispatch helper + the pure
// bulkDeltaFor/mergeStatsDelta math) was lifted into useEmails.stats.ts
// (zero behaviour change). Imported for the hooks below + re-exported so
// existing `import { mergeStatsDelta, dispatchEmailStatsDelta, ... }
// from '.../useEmails'` paths stay byte-identical.
import {
  EMAIL_STATS_OPTIMISTIC_DELTA_EVENT,
  dispatchEmailStatsDelta,
  bulkDeltaFor,
  mergeStatsDelta,
} from './useEmails.stats'
import type { EmailStatsDelta } from './useEmails.stats'

export {
  EMAIL_STATS_OPTIMISTIC_DELTA_EVENT,
  dispatchEmailStatsDelta,
  bulkDeltaFor,
  mergeStatsDelta,
}
export type { EmailStatsDelta }


// Email stats
//
// 2026-05-28 — Phase 1 of the "poll on change, not on a timer" rebuild
// (companion BE change: ``organisation/views/email_views/stats_actions_views.py``
// gained a watermark-based ETag/304 short-circuit).
//
// The pre-MR shape was a blind 60s ``useVisiblePolling`` that hit 6
// COUNT queries on a 135 k-row mailbox (17–42 s wall time cold,
// stranding the sidebar badges behind a spinner). On an org where
// only 10–20 emails change per day, that's a thousand wasted re-counts
// for every meaningful update.
//
// Phase 1 replaces the timer-driven poll with two event-driven paths:
//   1. ``EMAIL_LIST_INVALIDATE_EVENT`` — fired by the sync-status hook
//      whenever the BE watermark advances. Same signal the inbox list
//      already listens to, so the stats and the list refresh together.
//   2. ``EMAIL_SYNC_QUEUED_EVENT`` — fired immediately when the
//      operator clicks Sync. Retained so the post-sync ladder still
//      catches the worker's write-commit window.
//
// Every refetch is conditional-GET: the hook tracks the last ETag and
// replays it as ``If-None-Match``. When the BE watermark is unchanged
// the response is a 0-DB-cost 304 — same accuracy as a full recount,
// just without the recount cost. See BUG_LEDGER:
// poll-on-change-not-on-timer for the failure mode this closes.
export function useEmailStats() {
  const [stats, setStats] = useState<EmailStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Last server-issued ETag. We hold this in a ref (not state) because
  // we don't want re-renders on each refetch tick — only when the
  // payload actually changes.
  const etagRef = useRef<string | null>(null)
  // Inflight throttle — same shape as ``useEmails`` uses for the
  // inbox list. Without it, rapid-fire EMAIL_LIST_INVALIDATE_EVENT
  // bursts (e.g. multi-account sync completing in close succession)
  // would stack overlapping fetches and the last one to land would
  // win — racing the optimistic local state.
  const inflightRef = useRef(false)

  const fetchStats = useCallback(async () => {
    if (inflightRef.current) return
    inflightRef.current = true
    setLoading(true)
    setError(null)
    try {
      const headers: Record<string, string> = {}
      if (etagRef.current) {
        headers['If-None-Match'] = etagRef.current
      }
      // BUG-2026-06-22-EMAIL-AGGREGATE-IDENTITY-RELOAD-LOOP (5th path in the
      // auth-pipeline-loops family). The inbox stats/badge-counts read
      // auto-fires on mount (useEffect below) + on every list-invalidate. On a
      // benign cookie(18)/bearer(19) drift the server stamps X-User-Id:18 vs
      // the tab pin 19 → the identity guard cleared the pin + scheduled a
      // non-converging reload → strobe. Aggregate counts are cosmetic display
      // data and this read already fails soft, so it opts out of the reload
      // guard. Genuine drift is still caught by the email LIST/DETAIL reads
      // (which keep the guard) + the IdentityMismatchBanner.
      const response = await emailApiFetch(
        '/api/emails/stats/', { headers, skipIdentityAssert: true },
      )
      // 304 Not Modified — server confirms the watermark hasn't
      // advanced. Keep the existing payload; do NOT clear or refetch.
      // This is the hot path on an idle inbox tab.
      if (response.status === 304) {
        return
      }
      if (!response.ok) {
        throw new Error(`Stats fetch failed: ${response.status}`)
      }
      // Capture the fresh ETag BEFORE parsing — even if the parse
      // fails (truncated body, etc.) we still want the ETag for the
      // next conditional-GET to short-circuit on no-change.
      const newEtag = response.headers.get('ETag')
      if (newEtag) {
        etagRef.current = newEtag
      }
      const text = await response.text()
      if (text) {
        try {
          const result = JSON.parse(text) as EmailStats
          if (result && typeof result === 'object') {
            setStats(result)
          }
        } catch {
          // Parse failure — keep the prior payload. The ETag has
          // already been updated so the next tick will either get a
          // 304 (and we'll stay on the prior payload) or a fresh
          // valid JSON (and we'll catch up). Either way we avoid
          // wiping the badges on a transient parse error.
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats')
    } finally {
      setLoading(false)
      inflightRef.current = false
    }
  }, [])

  useEffect(() => { fetchStats() }, [fetchStats])

  // Soft auto-retry after a failed refresh so a transient 5xx/timeout
  // does not leave the inbox permanently alarmed. Keeps last-good
  // stats painted; the loud FE banner only shows when stats is null.
  useEffect(() => {
    if (!error) return
    const t = window.setTimeout(() => { void fetchStats() }, 8_000)
    return () => window.clearTimeout(t)
  }, [error, fetchStats])

  // ── Event-driven refresh path #1: BE watermark advanced ──
  // ``EMAIL_LIST_INVALIDATE_EVENT`` is dispatched by the sync-status
  // hook whenever the BE's last ``finished_at`` advances. The inbox
  // list listens to the same event; we use the same 750 ms debounce
  // so a burst of invalidations (e.g. multiple accounts settling in
  // close succession) collapses to one fetch. The fetch itself is
  // cheap on a no-change tick (server-side 304) so even a chatty
  // event source is safe.
  useEffect(() => {
    let debounceTimer: number | null = null
    const onInvalidate = () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer)
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null
        fetchStats()
      }, 750)
    }
    window.addEventListener(
      EMAIL_LIST_INVALIDATE_EVENT, onInvalidate as EventListener,
    )
    return () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer)
        debounceTimer = null
      }
      window.removeEventListener(
        EMAIL_LIST_INVALIDATE_EVENT, onInvalidate as EventListener,
      )
    }
  }, [fetchStats])

  // ── Event-driven refresh path #2: FE-side "sync queued" ──
  // When a sync is dispatched to Celery (Quotech-backend returns 202
  // with a task_id) the worker processes the email pull asynchronously
  // — the local DB grows over the next 5–60 s as Graph pages get
  // ingested. The sync-status hook will eventually fire
  // EMAIL_LIST_INVALIDATE_EVENT once the watermark advances, but we
  // also re-poll on a short ladder so the visible count starts moving
  // BEFORE the watermark settles (Jake 2026-05-04: "It did not
  // download the emails?" — yes, they were syncing, we just weren't
  // showing it fast enough to convince him).
  //
  // Shorter ladder than pre-MR ([4 s, 12 s, 30 s, 60 s]) because each
  // tick is now a 304 if nothing changed — the cost is the
  // 5 s-cached watermark probe, nothing more. Two ticks are enough
  // to cover the worker's typical write-commit window (sub-second
  // for the first chunk, ~10 s for a full mailbox).
  useEffect(() => {
    // Timer ids tracked in a Set so the cleanup actually cancels them —
    // the old version stashed them on the handler function and leaked
    // both timers on unmount (fetchStats fired into a dead component).
    const timers = new Set<number>()
    const onQueued = () => {
      const t1 = window.setTimeout(() => { timers.delete(t1); fetchStats() }, 4_000)
      const t2 = window.setTimeout(() => { timers.delete(t2); fetchStats() }, 15_000)
      timers.add(t1)
      timers.add(t2)
    }
    window.addEventListener(EMAIL_SYNC_QUEUED_EVENT, onQueued as EventListener)
    return () => {
      for (const t of timers) window.clearTimeout(t)
      timers.clear()
      window.removeEventListener(EMAIL_SYNC_QUEUED_EVENT, onQueued as EventListener)
    }
  }, [fetchStats])

  // NOTE: the prior blind 60s visible-polling call (the timer-driven
  // stats refetch) was REMOVED in this MR. Stats are now strictly
  // event-driven (BE watermark bump or FE-dispatched sync queued).
  // A future refactor that re-adds a blind timer here would revive
  // the inbox-hang-on-poll failure mode — the cadence pin test
  // (``useEmailStats.cadence.pin.test.ts``) catches that regression
  // at PR review time.

  // ── Phase 2: optimistic delta listener ───────────────────────
  // ``useEmailActions`` dispatches a delta event BEFORE awaiting
  // the BE mutation (mark-read / star / archive / bulk). We apply
  // it to local state immediately so the operator sees the badge
  // move in the same paint as the row state. On error the action
  // wrapper dispatches an inverse delta to revert.
  //
  // Accuracy guarantee: addition is associative, so the order in
  // which deltas vs BE refetches arrive doesn't matter. When the
  // BE watermark next bumps, the fresh 200 payload OVERWRITES the
  // local payload — any temporary optimistic drift is bounded
  // (one mutation worth) and self-healing. The clamp-at-zero in
  // ``mergeStatsDelta`` protects against double-dispatch.
  useEffect(() => {
    const onDelta = (event: Event) => {
      const detail = (event as CustomEvent<EmailStatsDelta>).detail
      if (!detail || typeof detail !== 'object') return
      setStats((prev) => (prev ? mergeStatsDelta(prev, detail) : prev))
    }
    window.addEventListener(
      EMAIL_STATS_OPTIMISTIC_DELTA_EVENT, onDelta as EventListener,
    )
    return () => {
      window.removeEventListener(
        EMAIL_STATS_OPTIMISTIC_DELTA_EVENT, onDelta as EventListener,
      )
    }
  }, [])

  return { stats, loading, error, refetch: fetchStats }
}

// Email actions
//
// Phase 2 (2026-05-28): every mutation that can change a stats badge
// dispatches an OPTIMISTIC ``EmailStatsDelta`` before awaiting the BE.
// The ``useEmailStats`` listener applies it to local state immediately
// so the operator sees the badge move in the same paint as the row
// state — no waiting for the next watermark bump (which only fires on
// sync completion, not on user mutations).
//
// Rollback shape: each wrapper computes both ``delta`` (forward) and
// ``rollback`` (inverse). On error after dispatching the forward
// delta, the wrapper dispatches the rollback so the badge un-moves.
// Accuracy is preserved by the clamp-at-zero in ``mergeStatsDelta`` +
// the eventual watermark reconciliation (next sync overwrites the
// payload from BE truth).
//
// The optional ``prevIsRead`` / ``prevIsStarred`` args let the caller
// skip the delta if the BE call is a no-op (e.g. ``markAsRead`` on
// an already-read row). Without prev-state knowledge we conservatively
// dispatch the delta — the worst case is "badge drifts by 1 for up to
// 60s until the response-cache TTL expires", which is bounded and
// self-healing.
// State-only bulk verbs that are safe to fan out to hidden mailbox
// copies of a deduped conversation (see syncDuplicates). Link / category
// verbs are intentionally excluded — they already propagate cross-mailbox
// on the backend (link_emails_to_job / link_emails_to_quote), so fanning
// them out here would double-process.
const DUPLICATE_SYNC_ACTIONS = new Set([
  'mark_read', 'mark_unread', 'star', 'unstar',
  'archive', 'unarchive', 'delete', 'restore', 'junk',
])

export function useEmailActions() {
  // markAsRead sets is_read=true. Delta is unread: -1 only if the row
  // WAS unread (prevIsRead === false). If prevIsRead is unknown
  // (undefined), dispatch conservatively — re-marking an already-read
  // row produces a -1 drift bounded by the 60s response-cache TTL.
  const markAsRead = useCallback(async (
    emailId: string, prevIsRead?: boolean,
  ) => {
    const willChange = prevIsRead !== true
    if (willChange) dispatchEmailStatsDelta({ unread: -1 })
    try {
      return await emailApi<Email>(`/api/emails/${emailId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ is_read: true }),
      })
    } catch (err) {
      if (willChange) dispatchEmailStatsDelta({ unread: 1 })
      throw err
    }
  }, [])

  // toggleStar flips is_starred. ``isStarred`` is the prev state — the
  // BE will write ``!isStarred``. Delta is +1 if starring, -1 if
  // unstarring. Always known (no "unknown prev" case here).
  const toggleStar = useCallback(async (
    emailId: string, isStarred: boolean,
  ) => {
    const delta = isStarred ? -1 : 1
    dispatchEmailStatsDelta({ starred: delta })
    try {
      return await emailApi<Email>(`/api/emails/${emailId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ is_starred: !isStarred }),
      })
    } catch (err) {
      dispatchEmailStatsDelta({ starred: -delta })
      notify({
        title: isStarred ? "Couldn't unstar the email" : "Couldn't star the email",
        kind: 'error',
        source: 'email',
      })
      throw err
    }
  }, [])

  // archive moves the email out of ``inbox`` into ``archive``. The BE
  // stats ``unprocessed`` counter is inbox-scoped — archiving an
  // unprocessed inbox row drops it. ``by_folder`` shifts one count
  // inbox→archive. Without per-email state we can't know
  // ``unprocessed`` impact precisely; pass ``prevState`` to be exact.
  const archive = useCallback(async (
    emailId: string,
    prevState?: { folder?: string; isRead?: boolean; isStarred?: boolean; complete?: boolean },
  ) => {
    const prevFolder = prevState?.folder ?? 'inbox'
    const wasUnread = prevState?.isRead === false
    const delta: EmailStatsDelta = {
      by_folder: { [prevFolder]: -1, archive: 1 },
    }
    if (wasUnread) delta.unread = -1  // archiving an unread row drops it from the unread tile
    dispatchEmailStatsDelta(delta)
    try {
      // ``complete: true`` (the reader's "Done" action) tells the backend to
      // ALSO move the linked Kanban card to its DONE lane, not just tombstone
      // it (operator 2026-06-14). Plain Archive (toolbar) omits it.
      return await emailApi<Email>(`/api/emails/${emailId}/`, {
        method: 'PATCH',
        body: JSON.stringify(
          prevState?.complete
            ? { is_archived: true, complete: true }
            : { is_archived: true },
        ),
      })
    } catch (err) {
      const revert: EmailStatsDelta = {
        by_folder: { [prevFolder]: 1, archive: -1 },
      }
      if (wasUnread) revert.unread = 1
      dispatchEmailStatsDelta(revert)
      notify({ title: "Couldn't archive the email", kind: 'error', source: 'email' })
      throw err
    }
  }, [])

  // unarchive moves the email OUT of ``archive`` back into ``inbox`` —
  // the inverse of ``archive`` above, used from the Archived view. The
  // by_folder delta shifts one count archive→inbox; an unread row also
  // re-enters the unread tile. ``complete`` is intentionally NOT sent:
  // un-archiving never reopens a linked Kanban card.
  const unarchive = useCallback(async (
    emailId: string,
    prevState?: { isRead?: boolean },
  ) => {
    const wasUnread = prevState?.isRead === false
    const delta: EmailStatsDelta = {
      by_folder: { archive: -1, inbox: 1 },
    }
    if (wasUnread) delta.unread = 1
    dispatchEmailStatsDelta(delta)
    try {
      return await emailApi<Email>(`/api/emails/${emailId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ is_archived: false }),
      })
    } catch (err) {
      const revert: EmailStatsDelta = {
        by_folder: { archive: 1, inbox: -1 },
      }
      if (wasUnread) revert.unread = -1
      dispatchEmailStatsDelta(revert)
      notify({ title: "Couldn't unarchive the email", kind: 'error', source: 'email' })
      throw err
    }
  }, [])

  // toggleRead is symmetric. ``isRead`` is the prev state — BE writes
  // ``!isRead``. Delta is unread: +1 if marking unread, -1 if marking
  // read. Always known.
  const toggleRead = useCallback(async (
    emailId: string, isRead: boolean,
  ) => {
    const delta = isRead ? 1 : -1  // becoming unread → +1; becoming read → -1
    dispatchEmailStatsDelta({ unread: delta })
    try {
      return await emailApi<Email>(`/api/emails/${emailId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ is_read: !isRead }),
      })
    } catch (err) {
      dispatchEmailStatsDelta({ unread: -delta })
      notify({
        title: isRead ? "Couldn't mark the email unread" : "Couldn't mark the email read",
        kind: 'error',
        source: 'email',
      })
      throw err
    }
  }, [])

  // bulkAction: the BE response carries ``affected`` — the actual
  // number of rows mutated. We dispatch the optimistic delta BEFORE
  // awaiting (using emailIds.length as a guess), then reconcile when
  // ``affected`` differs (apply the remainder as a corrective delta).
  // For actions whose stats impact is uncertain (set_category,
  // reset_category) we skip the delta and let the watermark catch up.
  const bulkAction = useCallback(async (
    emailIds: string[],
    action: string,
    extras: Record<string, unknown> = {},
  ) => {
    const guessCount = emailIds.length
    const forward = bulkDeltaFor(action, guessCount, extras)
    if (forward) dispatchEmailStatsDelta(forward)
    try {
      const result = await emailApi<{
        success: boolean; affected: number
      }>('/api/emails/bulk/', {
        method: 'POST',
        body: JSON.stringify({ email_ids: emailIds, action, ...extras }),
      })
      // Reconcile: if BE affected fewer rows than we optimistically
      // guessed, apply a corrective delta for the difference.
      if (forward && typeof result?.affected === 'number'
          && result.affected !== guessCount) {
        const correction = bulkDeltaFor(
          action, result.affected - guessCount, extras,
        )
        if (correction) dispatchEmailStatsDelta(correction)
      }
      return result
    } catch (err) {
      if (forward) {
        // Rollback by dispatching the inverse of the forward delta.
        const rollback = bulkDeltaFor(action, -guessCount, extras)
        if (rollback) dispatchEmailStatsDelta(rollback)
      }
      notify({
        title: `Couldn't complete the bulk action on ${guessCount} email${guessCount === 1 ? '' : 's'}`,
        kind: 'error',
        source: 'email',
      })
      throw err
    }
  }, [])

  /** Change #1210 — Public | Private. The server only accepts this from the
   *  mailbox owner (or a shared-mailbox grant); a 403 is surfaced as a
   *  toast and the caller keeps the old value. */
  const setVisibility = useCallback(async (
    emailId: string,
    visibility: 'public' | 'private',
  ) => {
    try {
      return await emailApi<Email>(`/api/emails/${emailId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ visibility }),
      })
    } catch (err) {
      notify({
        title: "Couldn't change who sees this email. Only the mailbox owner can switch it.",
        kind: 'error',
        source: 'email',
      })
      throw err
    }
  }, [])

  /** Pin a category on a single email. Backend creates / updates the
   *  EmailCategoryOverride sidecar so the AI classifier won't undo this
   *  on the next sync / Recategorise pass. */
  const setCategory = useCallback(async (
    emailId: string,
    category: string,
  ) => {
    return emailApi<Email>(`/api/emails/${emailId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ category }),
    })
  }, [])

  /** Force "this is a quote request" — the quote-request analogue of
   *  Allocate-to-Bills. Unlike setCategory (which only flips the tab),
   *  the backend ALSO dispatches the extraction pipeline that builds the
   *  QuoteRequestExtraction row the tracker reads + the QDrive/draft
   *  cascade. Writes a locked override so the AI can't undo it. */
  const markAsQuoteRequest = useCallback(async (emailId: string) => {
    return emailApi<{
      email_id: string
      category: string
      extraction_queued: boolean
      already_quote_request: boolean
    }>(`/api/emails/${emailId}/mark-as-quote-request/`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }, [])

  /** Bulk "Set category" — applies the override + locks. */
  const bulkSetCategory = useCallback(async (
    emailIds: string[],
    category: string,
    locked: boolean = true,
  ) => {
    return emailApi<{ success: boolean; affected: number }>('/api/emails/bulk/', {
      method: 'POST',
      body: JSON.stringify({
        email_ids: emailIds,
        action: 'set_category',
        category,
        locked,
      }),
    })
  }, [])

  /** Bulk "Reset to AI" — drops overrides and re-queues classification. */
  const bulkResetCategory = useCallback(async (emailIds: string[]) => {
    return emailApi<{ success: boolean; affected: number }>('/api/emails/bulk/', {
      method: 'POST',
      body: JSON.stringify({
        email_ids: emailIds,
        action: 'reset_category',
      }),
    })
  }, [])

  /** Junk + auto-trash a sender. Creates (or revives) an auto-junk rule
   *  from this email's sender, retroactively soft-bins existing matches,
   *  and arms auto-trash for future mail. ``matchType`` defaults to the
   *  sending sub-domain; pass 'sender_exact' for just this address.
   *  Returns the created rule + how many existing emails were binned. */
  const createAutoJunkRule = useCallback(async (
    emailId: string,
    matchType: 'sender_domain' | 'sender_exact' = 'sender_domain',
  ) => {
    return emailApi<{
      success: boolean
      created: boolean
      binned_count: number
      rule: {
        id: number
        match_type: string
        match_value: string
        label: string
        enabled: boolean
        active_binned_count: number
      }
    }>(`/api/emails/${emailId}/auto-junk-rule/`, {
      method: 'POST',
      body: JSON.stringify({ match_type: matchType }),
    })
  }, [])

  /** One-shot direction backfill — flips outbound bills to invoices and
   *  vice-versa for the requesting user's organisation. Wired up from
   *  the Recategorise menu button. */
  const backfillDirections = useCallback(async () => {
    return emailApi<{
      flipped_to_invoice?: number
      flipped_to_bill?: number
      own_domains?: string[]
    }>('/api/emails/backfill-directions/', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }, [])

  // ---------------------------------------------------------------
  // Phase 10 — assignment + watcher mutations.
  // ---------------------------------------------------------------
  /** PATCH the EmailAssignment sidecar (assignee + bucket +
   *  optional explicit scheduled_for). The BE applies the
   *  bucket → scheduled_for derivation when scheduled_for is
   *  omitted. Pass ``null`` to a field to leave it unchanged
   *  (the BE treats undefined-or-omitted as "don't touch"). */
  const setEmailAssignment = useCallback(
    async (
      emailId: string,
      patch: {
        assignee_user_id?: number | null
        bucket?: 'no_date' | 'today' | 'tomorrow' | 'this_week'
        scheduled_for?: string | null
        notes?: string
      },
    ) => {
      return emailApi<EmailAssignment>(
        `/api/emails/${emailId}/assignment/`,
        {
          method: 'PATCH',
          body: JSON.stringify(patch),
        },
      )
    },
    [],
  )

  /** Clear the assignment entirely (operator un-allocates the
   *  email). DELETE on the sidecar — CASCADE removes the row. */
  const clearEmailAssignment = useCallback(
    async (emailId: string) => {
      return emailApi<void>(
        `/api/emails/${emailId}/assignment/`,
        { method: 'DELETE' },
      )
    },
    [],
  )

  /** Add a watcher (right-click "Invite team member"). The BE
   *  is idempotent — re-adding a removed watcher revives the
   *  row instead of duplicating. */
  const inviteEmailWatcher = useCallback(
    async (
      emailId: string,
      userId: number,
      opts: { role?: 'viewer' | 'replier'; note?: string } = {},
    ) => {
      return emailApi<EmailWatcher>(
        `/api/emails/${emailId}/watchers/`,
        {
          method: 'POST',
          body: JSON.stringify({
            user_id: userId,
            role: opts.role ?? 'replier',
            note: opts.note ?? '',
          }),
        },
      )
    },
    [],
  )

  /** Remove a watcher (soft delete server-side). */
  const removeEmailWatcher = useCallback(
    async (emailId: string, userId: number) => {
      return emailApi<void>(
        `/api/emails/${emailId}/watchers/${userId}/`,
        { method: 'DELETE' },
      )
    },
    [],
  )

  // BUG-2026-06-25-EMAIL-MULTI-MAILBOX-ACTIONS: push a state change to the
  // HIDDEN sibling copies of a deduped conversation so an action on the one
  // visible inbox row doesn't leave the other mailboxes' copies
  // unread/active server-side (they'd reappear after the next sync). The
  // visible survivor already drove the optimistic list + stats delta, so
  // this is a pure server-side consistency sync: NO extra delta (avoids
  // double-counting), and a failure is swallowed (the next stats refetch /
  // sync reconciles — the operator already saw their action succeed).
  const syncDuplicates = useCallback(
    async (siblingIds: string[], action: string) => {
      if (!Array.isArray(siblingIds) || siblingIds.length === 0) return
      if (!DUPLICATE_SYNC_ACTIONS.has(action)) return
      try {
        await emailApi('/api/emails/bulk/', {
          method: 'POST',
          body: JSON.stringify({ email_ids: siblingIds, action }),
        })
      } catch {
        /* best-effort cross-mailbox sync — next refetch reconciles */
      }
    },
    [],
  )

  return {
    markAsRead, toggleStar, archive, unarchive, toggleRead,
    bulkAction, setCategory, setVisibility, markAsQuoteRequest, bulkSetCategory, bulkResetCategory,
    createAutoJunkRule,
    backfillDirections,
    setEmailAssignment, clearEmailAssignment,
    inviteEmailWatcher, removeEmailWatcher,
    syncDuplicates,
  }
}

// ---------------------------------------------------------------------------
// Auto-junk rules — list / toggle / revert / delete + per-rule event log.
// Powers the Tags & Rules settings "Auto-junk" tab (the reversion log).
// ---------------------------------------------------------------------------
export interface AutoJunkRule {
  id: number
  match_type: 'sender_domain' | 'sender_exact'
  match_value: string
  label: string
  enabled: boolean
  fire_count: number
  active_binned_count: number
  last_fired_at: string | null
  created_at: string | null
}

export interface AutoJunkEvent {
  id: number
  email_id: string
  subject: string
  from_address: string
  scope: 'manual' | 'retroactive' | 'auto_sync'
  reverted: boolean
  created_at: string | null
  reverted_at: string | null
}

export function useAutoJunkRules() {
  const [rules, setRules] = useState<AutoJunkRule[]>([])
  const [loading, setLoading] = useState(true)

  const fetchRules = useCallback(async () => {
    setLoading(true)
    try {
      const data = await emailApi<AutoJunkRule[]>('/api/emails/auto-junk-rules/')
      setRules(Array.isArray(data) ? data : [])
    } catch (err) {
      console.warn('Failed to fetch auto-junk rules:', err instanceof Error ? err.message : err)
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchRules() }, [fetchRules])

  const toggleRule = useCallback(async (ruleId: number, enabled: boolean) => {
    const updated = await emailApi<AutoJunkRule>(
      `/api/emails/auto-junk-rules/${ruleId}/`,
      { method: 'PATCH', body: JSON.stringify({ enabled }) },
    )
    setRules(curr => curr.map(r => r.id === ruleId ? updated : r))
    return updated
  }, [])

  /** Restore every binned email for the rule + disable it (keeps log). */
  const revertRule = useCallback(async (ruleId: number) => {
    const res = await emailApi<{ restored_count: number; rule: AutoJunkRule }>(
      `/api/emails/auto-junk-rules/${ruleId}/revert/`,
      { method: 'POST', body: JSON.stringify({}) },
    )
    setRules(curr => curr.map(r => r.id === ruleId ? res.rule : r))
    return res
  }, [])

  /** Restore + remove the rule entirely. */
  const deleteRule = useCallback(async (ruleId: number) => {
    const prev = rules
    setRules(curr => curr.filter(r => r.id !== ruleId))
    try {
      return await emailApi<{ restored_count: number }>(
        `/api/emails/auto-junk-rules/${ruleId}/`,
        { method: 'DELETE' },
      )
    } catch (err) {
      setRules(prev)
      throw err
    }
  }, [rules])

  const fetchEvents = useCallback(async (ruleId: number) => {
    return emailApi<AutoJunkEvent[]>(
      `/api/emails/auto-junk-rules/${ruleId}/events/`,
    )
  }, [])

  return { rules, loading, fetchRules, toggleRule, revertRule, deleteRule, fetchEvents }
}

// Email tags — shared react-query catalog (BL-3). Body lives in useEmailTags.ts
// so this monolith is not the mount-fetch owner.
export { useEmailTags } from './useEmailTags'

// Link email to job
export function useEmailJobLink() {
  const [isLinking, setIsLinking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const linkToJob = useCallback(async (emailId: string, jobId: number, _linkType: string = 'general') => {
    setIsLinking(true)
    setError(null)
    try {
      const result = await emailApi<{ success: boolean; affected: number }>('/api/emails/bulk/', {
        method: 'POST',
        body: JSON.stringify({ email_ids: [emailId], action: 'link_job', job_id: jobId }),
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link email to job')
      return null
    } finally {
      setIsLinking(false)
    }
  }, [])

  /**
   * rejectJob — operator dismisses a bogus job allocation on an email
   * row. Same blast radius as unlink_job (clears Email.job_id) BUT
   * also writes an EmailJobRejection denylist row so the matcher
   * stops re-suggesting the same (sender, job) pair on future emails
   * from this sender (or sender domain).
   *
   * Built for 2026-05-15 operator report ("AI automatically allocated
   * the email to the fucking job.. I can't even reject it so you can
   * get it trained and remove the tag.."). The 2026-05-15 hotfix
   * stopped the auto-classifier from silently writing job_id; this
   * hook gives the operator the cleanup tool for the historical
   * misallocations PLUS the training signal for the matcher.
   *
   * Backend contract:
   *   POST /api/emails/bulk/
   *   { email_ids: [<uuid>], action: 'reject_job' }
   *   → {
   *       success,
   *       action,
   *       affected,             // emails whose job_id was cleared
   *                             // by the originating rejection
   *       feedback_created,     // new EmailJobRejection rows
   *       cascade_cleared,      // OTHER emails in the same org that
   *                             // shared the same (sender, job) pair
   *                             // and were cleared in the same atomic
   *                             // block — 2026-05-15 follow-up
   *       errors[]
   *     }
   */
  const rejectJob = useCallback(async (emailId: string) => {
    setIsLinking(true)
    setError(null)
    try {
      const result = await emailApi<{
        success: boolean
        affected: number
        feedback_created: number
        cascade_cleared: number
        errors: string[]
      }>('/api/emails/bulk/', {
        method: 'POST',
        body: JSON.stringify({ email_ids: [emailId], action: 'reject_job' }),
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject job allocation')
      return null
    } finally {
      setIsLinking(false)
    }
  }, [])

  /**
   * confirmJob — operator blesses an AI auto-allocated job link (the ✓
   * counterpart to rejectJob's ✕). Keeps Email.job_id and flips the
   * EmailJobAutoLink provenance to `confirmed` server-side, so the
   * row's ✓/✕ review affordance disappears on the next refetch.
   *
   * Backend contract:
   *   POST /api/emails/bulk/
   *   { email_ids: [<uuid>], action: 'confirm_job' }
   *   → { success, action, affected }   // affected = links confirmed
   */
  const confirmJob = useCallback(async (emailId: string) => {
    setIsLinking(true)
    setError(null)
    try {
      const result = await emailApi<{
        success: boolean
        affected: number
      }>('/api/emails/bulk/', {
        method: 'POST',
        body: JSON.stringify({ email_ids: [emailId], action: 'confirm_job' }),
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm job allocation')
      return null
    } finally {
      setIsLinking(false)
    }
  }, [])

  const getLinks = useCallback(async (emailId: string) => {
    try {
      const result = await emailApi<{ links: any[] }>(`/api/emails/${emailId}/links/`)
      return result.links || []
    } catch {
      return []
    }
  }, [])

  /**
   * rejectSuggestion — operator dismisses an AI suggestion chip on an
   * UNALLOCATED row. Different endpoint to `rejectJob`: rejectJob
   * clears an existing Email.job_id AND writes a denylist row, but
   * requires the email to be currently allocated. For suggestion
   * chips the email is by definition unallocated (the chip is the
   * pre-confirmation surface) so we hit the dedicated endpoint that
   * writes the denylist row WITHOUT touching allocation state.
   *
   * Backend contract:
   *   POST /api/emails/job-suggestion-rejections/
   *   { email_id: <uuid>, job_id: <int> }
   *   → { rejected, rejected_job_id, feedback_created,
   *       feedback_id, cascade_cleared }
   *
   * The denylist row is keyed on (sender_address OR sender_domain,
   * job_id) so the matcher + thread inheritance both stop surfacing
   * the same wrong pair on future emails from this sender. The chip
   * caller should also drop the local row from its session map so
   * the chip disappears immediately (the next BE refetch will
   * confirm).
   */
  const rejectSuggestion = useCallback(
    async (emailId: string, jobId: number) => {
      setIsLinking(true)
      setError(null)
      try {
        const result = await emailApi<{
          rejected: boolean
          rejected_job_id: number
          feedback_created: boolean
          feedback_id: number | null
          cascade_cleared: number
        }>('/api/emails/job-suggestion-rejections/', {
          method: 'POST',
          body: JSON.stringify({ email_id: emailId, job_id: jobId }),
        })
        return result
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to dismiss suggestion',
        )
        return null
      } finally {
        setIsLinking(false)
      }
    },
    [],
  )

  return { linkToJob, rejectJob, confirmJob, rejectSuggestion, getLinks, isLinking, error }
}

/**
 * useEmailJobSuggestions — pull the top scored jobs the backend
 * thinks this email should be linked to. Mirrors LinkToJobModal's
 * scoring so the auto-suggest list and the manual picker agree.
 */
export interface EmailJobSuggestion {
  job_id: number
  job_number: string
  client_name: string
  site_name: string
  status: string
  score: number
  is_best: boolean
}

export interface EmailJobSuggestionsResponse {
  email_id: string
  auto_link_threshold: number
  current_job_id?: number | null
  suggestions: EmailJobSuggestion[]
}

export function useEmailJobSuggestions(emailId: string | null, enabled = true) {
  const [data, setData] = useState<EmailJobSuggestionsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!emailId || !enabled) return
    setLoading(true)
    setError(null)
    try {
      const result = await emailApi<EmailJobSuggestionsResponse>(
        `/api/emails/${emailId}/job-suggestions/`,
      )
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions')
    } finally {
      setLoading(false)
    }
  }, [emailId, enabled])

  useEffect(() => { void fetch() }, [fetch])

  return { data, loading, error, refetch: fetch }
}

// AI analysis
export function useEmailAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const analyze = useCallback(async (emailId: string, force = true) => {
    setIsAnalyzing(true)
    setError(null)
    try {
      const result = await emailApi<{
        category: string
        confidence: number
        summary: string
        entities: any
        suggested_actions: { action: string; label: string; priority: string }[]
      }>(`/api/emails/${emailId}/analyze/`, {
        method: 'POST',
        body: JSON.stringify({ force }),
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed')
      return null
    } finally {
      setIsAnalyzing(false)
    }
  }, [])

  return { analyze, isAnalyzing, error }
}

// ---------------------------------------------------------------------------
// useClassifyUnprocessed — kick off a backend backfill that classifies
// every "uncategorised" email the user can see (AI-tagging them as
// quote_request / invoice / variation / etc). Returns how many were
// queued so the UI can show a toast with concrete numbers.
//
// Why a hook + dedicated endpoint instead of looping the analyze endpoint
// from the client: see EmailClassifyUnprocessedView's docstring — the
// per-email analyze path is synchronous and rate-limit-bound by the
// classifier; a Celery batch is the safe way to backfill an inbox.
// ---------------------------------------------------------------------------
export function useClassifyUnprocessed() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(
    async (
      limit: number = 200,
      includeGeneral: boolean = false,
      recategorize: boolean = false,
    ) => {
      setBusy(true)
      setError(null)
      try {
        const result = await emailApi<{
          queued: number
          scope: string
          limit: number
          recategorize?: boolean
        }>('/api/emails/classify-unprocessed/', {
          method: 'POST',
          body: JSON.stringify({
            limit,
            include_general: includeGeneral,
            recategorize,
          }),
        })
        return result
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Classification request failed'
        setError(msg)
        return null
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  return { run, busy, error }
}

// Outlook connection status
export type OutlookStatus = {
  connected: boolean | null
  email: string | null
  isExpired: boolean
  expiresInSeconds: number | null
  authorizeUrl: string | null
  message: string | null
  lastCheckedAt: number | null
  reachable: boolean
  // True when ANY user in the current organisation has an Outlook
  // token attached — even if THIS user has not personally connected.
  // The integrations banner uses this to suppress the nag for
  // operators / partners viewing a tenant whose principal has
  // already authorised email. (qb!2796 / qf!2795)
  orgConnected: boolean
}

const OUTLOOK_STATUS_EVENT = 'outlook:status-changed'
const REFRESH_INTERVAL_MS = 60_000
// Warn ~5min before expiry so the user can act before an in-flight send fails.
const EXPIRY_WARN_WINDOW_SEC = 300

/** Consume `?outlook=connected|error&reason=...` search params left by the
 *  Quotech OAuth callback, surface a toast, and scrub them from the URL so a
 *  refresh doesn't re-fire the notification. */
function consumeOAuthResultFromUrl(): { kind: 'connected' | 'error'; reason?: string } | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const raw = params.get('outlook')
  if (raw !== 'connected' && raw !== 'error') return null
  const reason = params.get('reason') || undefined
  params.delete('outlook')
  params.delete('reason')
  const query = params.toString()
  const clean = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  window.history.replaceState({}, '', clean)
  return raw === 'connected' ? { kind: 'connected' } : { kind: 'error', reason }
}

type OutlookStatusPayload = {
  connected?: boolean
  email?: string
  is_expired?: boolean
  expires_in_seconds?: number | null
  authorize_url?: string
  message?: string
  org_connected?: boolean
}

/**
 * Pure I→O: build the `/api/outlook/authorize/` query string.
 *
 * Mirrors {@link buildGmailAuthorizeQuery}. `origin` is the current page
 * URL (so the BE can redirect back). A `loginHint` is forwarded ONLY when
 * it looks like an email, so Microsoft pre-selects the right account in
 * its picker on reconnect — the BE keeps `prompt=select_account`, so the
 * hint only pre-fills (the user can still correct it). Anything without an
 * `@` is ignored (first-time connect passes none).
 * (BUG-2026-06-15-EMAIL-SHARED-MAILBOX-RECONNECT)
 */
export function buildOutlookAuthorizeQuery(
  currentUrl: string,
  loginHint?: string,
): string {
  const origin = encodeURIComponent(currentUrl)
  // `typeof` guard: `authorize` is also bound directly to onClick in places
  // (e.g. EmailLayout's sidebar connect button), so the first arg can be a
  // MouseEvent rather than a string. Only a real email string becomes a hint.
  const hint =
    typeof loginHint === 'string' && loginHint.includes('@')
      ? `&login_hint=${encodeURIComponent(loginHint)}`
      : ''
  return `origin=${origin}${hint}`
}

export function useOutlookStatus() {
  const [status, setStatus] = useState<OutlookStatus>({
    connected: null,
    email: null,
    isExpired: false,
    expiresInSeconds: null,
    authorizeUrl: null,
    message: null,
    lastCheckedAt: null,
    reachable: true,
    orgConnected: false,
  })

  const check = useCallback(async () => {
    try {
      // BUG-2026-06-18-GMAIL-STATUS-IDENTITY-RELOAD-LOOP (recurrence of
      // BUG-2026-06-10-VIEWPREF-IDENTITY-RELOAD-LOOP): this is a fail-soft,
      // NON-data connection-status poll — it fires on mount + visibility +
      // focus + interval. On a benign identity drift (tab pinned to 19,
      // server stamps 18) it must NEVER trip quotechFetch's tab-wide
      // identity-mismatch RELOAD, or the poll → reload → re-mount → poll
      // strobe seizes the tab until the circuit breaker trips. Genuine
      // drift is still surfaced by every data-bearing call + the
      // IdentityMismatchBanner. `skipIdentityAssert` mirrors the
      // view-preference engine's opt-out.
      const data = await emailApi<OutlookStatusPayload>('/api/outlook/status/', {
        skipIdentityAssert: true,
      })
      // No client-side retry of /api/outlook/refresh/ here.
      //
      // BUG-2026-05-23-OUTLOOK-REFRESH-400-SPAM:
      // Earlier revisions of this hook POSTed /api/outlook/refresh/
      // whenever GET /api/outlook/status/ returned is_expired:true,
      // with the intent of a one-shot silent recovery before showing
      // the reconnect CTA. That retry was dead code on the current
      // backend:
      //   * OutlookStatusView already calls
      //     OutlookTokenManager.has_valid_token() inside
      //     _try_auto_refresh (status_invite_refresh.py L134-L135 /
      //     L163-L174). If the silent refresh succeeds, is_expired
      //     comes back false and this branch never fires.
      //   * If the silent refresh fails (invalid_grant, no refresh
      //     token, or transient I/O), is_expired stays true — and
      //     OutlookRefreshTokenView runs the SAME has_valid_token()
      //     call against the SAME token state, so it deterministically
      //     returns 400 ("Token refresh failed. Please reconnect
      //     Outlook." or "No refresh token available.").
      // The net effect on this mount path was therefore: every status
      // check that saw an expired token produced a guaranteed 400 in
      // the Network panel (multiplied by the mount, visibility, focus,
      // interval, and cross-tab listeners below). Removing the retry
      // produces the honest outcome (reconnect CTA) without the noise.
      // If the BE ever exposes a refresh path that differs from the
      // status auto-refresh (e.g. a force-acquire-new-refresh-token
      // flow), wire that into a user gesture, not this background
      // poller.
      const next: OutlookStatus = {
        connected: !!data.connected,
        email: data.email ?? null,
        isExpired: !!data.is_expired,
        expiresInSeconds: data.expires_in_seconds ?? null,
        authorizeUrl: data.authorize_url ?? null,
        message: data.message ?? null,
        lastCheckedAt: Date.now(),
        reachable: true,
        orgConnected: !!data.org_connected,
      }
      setStatus((prev) => {
        const changed =
          prev.connected !== next.connected ||
          prev.isExpired !== next.isExpired ||
          prev.email !== next.email ||
          prev.orgConnected !== next.orgConnected
        if (changed) {
          window.dispatchEvent(new CustomEvent(OUTLOOK_STATUS_EVENT, { detail: next }))
        }
        return next
      })
      return next
    } catch {
      // BUG (connect-banner flap on a connected mailbox): a transient
      // status-fetch failure — a network blip, a 401 mid token-refresh,
      // a 5xx, or an aborted request on navigation — must NOT flip a
      // previously-connected user to "disconnected". The old code
      // overwrote connected/email/org with false/null on every failed
      // poll (mount + 60s interval + focus + visibility), which flashed
      // the "Connect your email" CTA at users who were in fact
      // connected (reported on a connected Outlook mailbox). Preserve
      // the last known connected/email/expiry/org state; only mark the
      // provider unreachable + stamp the check time. EmailConnectionBanner
      // treats an unreachable-but-known-connected provider as healthy.
      let next!: OutlookStatus
      setStatus((prev) => {
        next = {
          ...prev,
          reachable: false,
          lastCheckedAt: Date.now(),
          message: 'Could not reach Outlook status service',
        }
        return next
      })
      return next
    }
  }, [])

  useEffect(() => { check() }, [check])

  // Recheck on tab focus/visibility — catches the OAuth round-trip and any
  // expiry that happened while the tab was backgrounded. BUG-0026: `focus`
  // is gated on a recent hidden transition so right-click noise from
  // Cursor's native-dialog overrides doesn't kick off a status refetch on
  // every contextmenu (the resulting OutlookBanner mount/unmount was
  // making the email body reader "move up and down").
  useEffect(() => {
    let lastHiddenAt = 0
    const onVisibility = () => {
      if (document.hidden) {
        lastHiddenAt = Date.now()
      } else {
        check()
      }
    }
    const onFocus = () => {
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt < 30_000) check()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
    }
  }, [check])

  // Periodic refresh so an expiry that lands mid-session propagates without a
  // user gesture.
  useEffect(() => {
    const id = window.setInterval(check, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [check])

  // Cross-component sync: one open tab triggers check → every mounted instance
  // of this hook updates too (no prop drilling of the status).
  useEffect(() => {
    const onEvt = (e: Event) => {
      const detail = (e as CustomEvent<OutlookStatus>).detail
      if (detail) setStatus(detail)
    }
    window.addEventListener(OUTLOOK_STATUS_EVENT, onEvt as EventListener)
    return () => window.removeEventListener(OUTLOOK_STATUS_EVENT, onEvt as EventListener)
  }, [])

  // `loginHint` (the mailbox address) is forwarded on RECONNECT so
  // Microsoft pre-selects the right account — critical for reviving a dead
  // SHARED mailbox (hello@quotech.ai), which can ONLY be revived by signing
  // in as that identity. First-time connect passes none.
  // (BUG-2026-06-15-EMAIL-SHARED-MAILBOX-RECONNECT)
  const authorize = useCallback(async (loginHint?: string) => {
    const query = buildOutlookAuthorizeQuery(window.location.href, loginHint)
    const data = await emailApi<{ authorization_url: string }>(
      `/api/outlook/authorize/?${query}`,
    )
    // Display-mode aware (2026-07-08): same-window in a browser tab, new
    // context from an installed PWA so the app window is never navigated
    // out of scope (which stranded the callback in Chrome).
    if (data.authorization_url) navigateToOAuthProvider(data.authorization_url)
  }, [])

  // Back-compat with existing callers that only know about the boolean.
  return {
    ...status,
    check,
    authorize,
    expiringSoon:
      status.connected === true &&
      !status.isExpired &&
      status.expiresInSeconds !== null &&
      status.expiresInSeconds <= EXPIRY_WARN_WINDOW_SEC,
  }
}

/** Convenience hook for views that want to fire a toast every time the OAuth
 *  callback returns the user to the app. Pass in the app's `toast()` function. */
export function useOutlookOAuthResultToast(
  toast: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void,
  onConnected?: () => void,
) {
  useEffect(() => {
    const result = consumeOAuthResultFromUrl()
    if (!result) return
    if (result.kind === 'connected') {
      toast('Microsoft connected - your email is ready to send.', 'success')
      onConnected?.()
    } else {
      const reason = result.reason ? ` (${result.reason})` : ''
      toast(`Microsoft connection failed${reason}. Please try again.`, 'error')
    }
  }, [toast, onConnected])
}

// ---------------------------------------------------------------------------
// Gmail OAuth status — mirror of useOutlookStatus but for /api/gmail/*
// ---------------------------------------------------------------------------
//
// The two-provider banner (EmailConnectionBanner) needs both statuses so it
// can decide whether to show "Connect Microsoft", "Connect Gmail", or both.
// Each provider keeps its own focus/visibility refresh + cross-tab event so
// connecting one provider in another tab updates this tab's banner without
// a manual refresh.
const GMAIL_STATUS_EVENT = 'gmail:status-changed'

export type GmailStatus = {
  connected: boolean | null
  email: string | null
  isExpired: boolean
  expiresInSeconds: number | null
  lastCheckedAt: number | null
  reachable: boolean
  // True when ANY user in the current organisation has an active
  // Gmail account attached — even if THIS user has not personally
  // connected. The integrations banner uses this to suppress the
  // nag for operators / partners viewing a tenant whose principal
  // has already authorised email. (qb!2796 / qf!2795)
  orgConnected: boolean
}

type GmailStatusPayload = {
  connected?: boolean
  email?: string | null
  is_expired?: boolean
  expires_at?: string | null
  org_connected?: boolean
}

/**
 * Pure I→O: build the `/api/gmail/authorize/` query string.
 *
 * `origin` is the current page URL (so the BE can redirect back). A
 * `loginHint` is forwarded ONLY when it looks like an email, so Google
 * pre-selects the right account in its chooser on reconnect. Anything
 * without an `@` is ignored (first-time connect passes none).
 * (BUG-2026-06-15-GMAIL-RECONNECT-WRONG-ACCOUNT)
 */
export function buildGmailAuthorizeQuery(
  currentUrl: string,
  loginHint?: string,
): string {
  const origin = encodeURIComponent(currentUrl)
  const hint =
    loginHint && loginHint.includes('@')
      ? `&login_hint=${encodeURIComponent(loginHint)}`
      : ''
  return `origin=${origin}${hint}`
}

export function useGmailStatus() {
  const [status, setStatus] = useState<GmailStatus>({
    connected: null,
    email: null,
    isExpired: false,
    expiresInSeconds: null,
    lastCheckedAt: null,
    reachable: true,
    orgConnected: false,
  })

  const check = useCallback(async () => {
    try {
      // BUG-2026-06-18-GMAIL-STATUS-IDENTITY-RELOAD-LOOP: the exact path in
      // the operator's reload-loop trail. A fail-soft, NON-data status poll
      // (mount + visibility + focus + interval) must NOT trip quotechFetch's
      // tab-wide identity-mismatch reload on a benign 18-vs-19 drift, or it
      // strobes until the breaker trips. See the Outlook poller above.
      const data = await emailApi<GmailStatusPayload>('/api/gmail/status/', {
        skipIdentityAssert: true,
      })
      const expiresInSeconds = data.expires_at
        ? Math.max(0, Math.floor((new Date(data.expires_at).getTime() - Date.now()) / 1000))
        : null
      const next: GmailStatus = {
        connected: !!data.connected,
        email: data.email ?? null,
        isExpired: !!data.is_expired,
        expiresInSeconds,
        lastCheckedAt: Date.now(),
        reachable: true,
        orgConnected: !!data.org_connected,
      }
      setStatus((prev) => {
        const changed =
          prev.connected !== next.connected ||
          prev.isExpired !== next.isExpired ||
          prev.email !== next.email ||
          prev.orgConnected !== next.orgConnected
        if (changed) {
          window.dispatchEvent(new CustomEvent(GMAIL_STATUS_EVENT, { detail: next }))
        }
        return next
      })
      return next
    } catch {
      // Mirror of the Outlook catch above — preserve last-known-good
      // connected/email/expiry/org on a transient status-fetch failure
      // so a poll blip never flashes the "Connect Gmail" CTA at a user
      // who is in fact connected. Only mark unreachable + stamp time.
      let next!: GmailStatus
      setStatus((prev) => {
        next = {
          ...prev,
          reachable: false,
          lastCheckedAt: Date.now(),
        }
        return next
      })
      return next
    }
  }, [])

  useEffect(() => { check() }, [check])

  // BUG-0026: gate the `focus` listener on a recent hidden transition.
  // Bare focus events from Cursor's browser right-click noise no longer
  // re-check Gmail status and flicker GmailBanner. visibilitychange
  // still covers tab background/foreground; focus still covers the
  // OAuth redirect-and-return round-trip.
  useEffect(() => {
    let lastHiddenAt = 0
    const onVisibility = () => {
      if (document.hidden) {
        lastHiddenAt = Date.now()
      } else {
        check()
      }
    }
    const onFocus = () => {
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt < 30_000) check()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
    }
  }, [check])

  useEffect(() => {
    const id = window.setInterval(check, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [check])

  useEffect(() => {
    const onEvt = (e: Event) => {
      const detail = (e as CustomEvent<GmailStatus>).detail
      if (detail) setStatus(detail)
    }
    window.addEventListener(GMAIL_STATUS_EVENT, onEvt as EventListener)
    return () => window.removeEventListener(GMAIL_STATUS_EVENT, onEvt as EventListener)
  }, [])

  // `loginHint` (the mailbox address) is forwarded on RECONNECT so Google
  // pre-selects the right account in its chooser. Without it, reconnecting
  // one mailbox in a multi-Google-account browser session re-consented
  // whichever account was cached, never the intended one
  // (BUG-2026-06-15-GMAIL-RECONNECT-WRONG-ACCOUNT). First-time connect
  // passes no hint so the operator picks freely.
  const authorize = useCallback(async (loginHint?: string) => {
    const query = buildGmailAuthorizeQuery(window.location.href, loginHint)
    const data = await emailApi<{ authorization_url: string }>(
      `/api/gmail/authorize/?${query}`,
    )
    // Display-mode aware (2026-07-08) — see the Outlook authorize above.
    if (data.authorization_url) navigateToOAuthProvider(data.authorization_url)
  }, [])

  return {
    ...status,
    check,
    authorize,
    expiringSoon:
      status.connected === true &&
      !status.isExpired &&
      status.expiresInSeconds !== null &&
      status.expiresInSeconds <= EXPIRY_WARN_WINDOW_SEC,
  }
}

// Convenience hook for views that want to fire a toast every time the Gmail
// OAuth callback returns the user to the app. The BE callback redirects with
// either ?gmail_connected=true (success) or ?gmail_error=true&message=... (fail).
function consumeGmailOAuthResultFromUrl(): { kind: 'connected' | 'error'; reason?: string } | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const connected = params.get('gmail_connected')
  const errored = params.get('gmail_error')
  if (connected !== 'true' && errored !== 'true') return null
  const message = params.get('message') || undefined
  // Strip our params so a refresh doesn't re-fire the toast.
  ;['gmail_connected', 'gmail_error', 'message', 'email', 'return_to'].forEach((k) => params.delete(k))
  const query = params.toString()
  const clean = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  window.history.replaceState({}, '', clean)
  return connected === 'true' ? { kind: 'connected' } : { kind: 'error', reason: message }
}

export function useGmailOAuthResultToast(
  toast: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void,
  onConnected?: () => void,
) {
  useEffect(() => {
    const result = consumeGmailOAuthResultFromUrl()
    if (!result) return
    if (result.kind === 'connected') {
      toast('Gmail connected - your email is ready to send.', 'success')
      onConnected?.()
    } else {
      const reason = result.reason ? ` (${result.reason})` : ''
      toast(`Gmail connection failed${reason}. Please try again.`, 'error')
    }
  }, [toast, onConnected])
}

// ---------------------------------------------------------------------------
// useEmailAccounts — list of *all* mailboxes the current user has connected
// (or has been granted access to via EmailAccountAccess for shared inboxes).
//
// Drives the "which inbox is syncing" indicator + multi-mailbox switcher in
// the Email Hub left nav. Refreshes when the OAuth status changes (e.g. user
// just connected another account) and at a slow polling cadence so newly
// added shared-inbox grants show up without a page reload.
// ---------------------------------------------------------------------------

export interface EmailAccount {
  id: number
  email_address: string
  display_name: string
  provider: 'outlook' | 'gmail'
  account_type: 'personal' | 'shared'
  sync_enabled: boolean
  last_sync_at: string | null
  owner_name: string
  /** Quotech user id of the mailbox owner. May be undefined on
   *  responses from older backends — guard accordingly. */
  owner_user_id?: number
  /** EmailAccount id for a REAL connected mailbox; ``null`` for a
   *  synthetic LEGACY bucket synthesised from facets (a user who has
   *  synced mail but no EmailAccount row). This is what the mailbox
   *  visibility key (``mailboxKey``) prefers so two mailboxes owned by
   *  the same user are independently toggleable. */
  account_id?: number | null
  /** Whether THIS viewer may toggle the mailbox private/shared (owner OR
   *  org admin/owner — computed server-side). Absent on older backends, so
   *  consumers fall back to the owner check. */
  can_manage_privacy?: boolean
}

interface EmailAccountsPayload {
  accounts: EmailAccount[]
}

const ACCOUNTS_REFRESH_MS = 60_000

/** Options for {@link useEmailAccounts}. */
export interface UseEmailAccountsOptions {
  /** When `false`, the hook fetches nothing — no mount load, no OAuth-status
   *  refresh, no 60 s poll — and returns an empty list. Lets a consumer that
   *  only NEEDS the mailbox list situationally (e.g. the kanban email-meta
   *  resolver, which only labels recipient mailboxes when the board actually
   *  has email cards) avoid an idle `GET /api/email-accounts/` round-trip +
   *  poll. Defaults to `true` so every existing caller is unchanged. */
  enabled?: boolean
}

export function useEmailAccounts(options?: UseEmailAccountsOptions) {
  const enabled = options?.enabled ?? true
  // PERF-2026-07-08-EMAIL-BOOT-META-CACHE (audit R5): seed the mailbox
  // list from the last authoritative snapshot so the switcher paints
  // instantly (client p50 for the cold pair of calls was ~1.4s across
  // 64k samples/7d). Pure first-paint placeholder — the mount fetch
  // below revalidates and overwrites in place. Read once (lazy init);
  // identity-scoped + swept on logout by useEmails.metaCache.
  const [initialSeed] = useState<EmailAccount[] | null>(() =>
    enabled ? readEmailMeta<EmailAccount[]>('accounts') : null,
  )
  const [accounts, setAccounts] = useState<EmailAccount[] | null>(initialSeed)
  const [error, setError] = useState<string | null>(null)
  // Not "loading" when disabled — there's nothing in flight. A seeded
  // mount also skips the loading state: rows are on screen and the
  // background revalidation replaces them silently (same contract as
  // the inbox list seed).
  const [loading, setLoading] = useState(
    enabled && !(initialSeed && initialSeed.length > 0),
  )
  // True once rows (seed or authoritative) are on screen — later
  // reloads (60s poll, OAuth status events) must not flash the
  // switcher back into its loading state.
  const accountsOnScreenRef = useRef<boolean>(
    !!(initialSeed && initialSeed.length > 0),
  )

  const reload = useCallback(async () => {
    setLoading((prev) => (accountsOnScreenRef.current ? prev : true))
    try {
      // PERF-2026-07-08-EMAIL-BOOT-META-CACHE: the accounts fetch and
      // the facets enrichment are independent reads — fire them in
      // PARALLEL. Sequential awaits stacked their latencies into the
      // hub's perceived boot (~1.4s + ~1.5s client p50 cold).
      const [accountsResult, facetsResult] = await Promise.allSettled([
        emailApi<EmailAccountsPayload>('/api/email-accounts/'),
        emailApi<{
          mailboxes: Array<{
            account_id: number | null
            user_id: number | null
            email: string
            name: string
            count: number
          }>
          // BUG-2026-06-22-EMAIL-AGGREGATE-IDENTITY-RELOAD-LOOP: mailbox
          // discovery from facets is cosmetic, auto-fires on mount, and is
          // already fail-soft — opt out of the identity-reload guard.
        }>('/api/emails/facets/', { skipIdentityAssert: true }),
      ])
      // Accounts remain the authoritative list — a facets failure only
      // loses the cosmetic legacy-bucket enrichment (fail-soft as
      // before); an accounts failure is a real error.
      if (accountsResult.status === 'rejected') throw accountsResult.reason
      const data = accountsResult.value
      // Annotate as EmailAccount[] (account_id: number | null) so the
      // synthetic legacy-mailbox rows pushed below can carry
      // `account_id: null`; without the annotation TS infers
      // `account_id: number` from `a.id` and rejects the null bucket.
      const accts: EmailAccount[] = (data.accounts ?? []).map((a) => ({
        ...a,
        // A real connected mailbox's switcher key is its EmailAccount id.
        account_id: a.id,
      }))

      // Merge mailboxes discovered from synced email facets.
      // If a user has synced emails but no EmailAccount row (e.g. a
      // legacy OutlookToken connection), the facets endpoint still
      // returns a LEGACY bucket (``account_id: null``) for them. We
      // synthesise a switcher row so the MailboxSwitcher can render and
      // toggle it. We do NOT synthesise rows for facet *account*
      // entries — those are already authoritatively covered by
      // /api/email-accounts/ above (with full provider/sync metadata).
      if (facetsResult.status === 'fulfilled') {
        const facets = facetsResult.value
        const existingUserIds = new Set(accts.map((a) => a.owner_user_id).filter(Boolean))
        for (const mb of facets.mailboxes ?? []) {
          if (mb.account_id == null && mb.user_id && !existingUserIds.has(mb.user_id)) {
            accts.push({
              id: -mb.user_id,
              account_id: null,
              email_address: mb.email,
              display_name: mb.name || mb.email,
              provider: 'outlook',
              account_type: 'personal',
              sync_enabled: true,
              last_sync_at: null,
              owner_name: mb.name || mb.email.split('@')[0],
              owner_user_id: mb.user_id,
            })
            existingUserIds.add(mb.user_id)
          }
        }
      }

      setAccounts(accts)
      accountsOnScreenRef.current = true
      setError(null)
      // Persist the authoritative (post-enrichment) list for the next
      // mount's instant paint. Never persists the seed itself — this
      // line is only reached after a successful server response.
      persistEmailMeta('accounts', accts)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load mailboxes')
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    void reload()
  }, [reload, enabled])

  // Pick up new mailboxes after an OAuth round-trip without making the user
  // refresh the page.
  useEffect(() => {
    if (!enabled) return
    const onStatus = () => { void reload() }
    window.addEventListener(OUTLOOK_STATUS_EVENT, onStatus)
    // Gmail connects must refresh the switcher too (2026-07-08) — the
    // OAuth landing dispatches both provider events with no detail.
    window.addEventListener(GMAIL_STATUS_EVENT, onStatus)
    return () => {
      window.removeEventListener(OUTLOOK_STATUS_EVENT, onStatus)
      window.removeEventListener(GMAIL_STATUS_EVENT, onStatus)
    }
  }, [reload, enabled])

  useEffect(() => {
    if (!enabled) return
    const id = window.setInterval(reload, ACCOUNTS_REFRESH_MS)
    return () => window.clearInterval(id)
  }, [reload, enabled])

  // Refresh on return-to-app (operator 2026-07-08: "added a new mailbox
  // and it took time for it to reflect"). Adding a SECOND mailbox doesn't
  // flip the OUTLOOK_STATUS_EVENT booleans (already connected), so the
  // switcher used to wait out the 60s poll. The OAuth consent happens in
  // another tab/browser (always, for the installed PWA), so the moment
  // this window becomes visible again is exactly when a new account may
  // exist — reload immediately, throttled to one fetch per 10s so rapid
  // tab-switching doesn't hammer the endpoint.
  const lastVisibilityReloadRef = useRef(0)
  useEffect(() => {
    if (!enabled) return
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastVisibilityReloadRef.current < 10_000) return
      lastVisibilityReloadRef.current = now
      void reload()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [reload, enabled])

  return { accounts: accounts ?? [], loading, error, reload }
}

// ---------------------------------------------------------------------
// useEmailFacets — aggregated filter dropdown data
// ---------------------------------------------------------------------
// Drives the sidebar's Mailbox / Job / Sender / Category multi-select
// sections. Backed by GET /api/emails/facets/, which itself respects
// the same per-user visibility rule as the list endpoint — so a user
// only ever sees facet entries for rows they're allowed to view.
//
// Refetches on demand (the FilterSidebar calls ``reload`` whenever
// the user expands a facet section). Cached in component state so
// repeated open-close cycles don't re-hit the network.
export function useEmailFacets() {
  // PERF-2026-07-08-EMAIL-BOOT-META-CACHE (audit R5): seed the filter
  // sidebar's facet lists from the last authoritative snapshot so its
  // sections paint instantly; the mount fetch revalidates in place.
  const [initialSeed] = useState<EmailFacets | null>(() =>
    readEmailMeta<EmailFacets>('facets'),
  )
  const [facets, setFacets] = useState<EmailFacets | null>(initialSeed)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // BUG-2026-06-22-EMAIL-AGGREGATE-IDENTITY-RELOAD-LOOP: facet counts are
      // cosmetic filter-sidebar display data (auto-fires on mount) and this
      // read already fails soft — opt out of the identity-reload guard.
      const data = await emailApi<EmailFacets>('/api/emails/facets/', { skipIdentityAssert: true })
      setFacets(data ?? null)
      if (data) persistEmailMeta('facets', data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load filter facets')
      setFacets(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void reload() }, [reload])

  return { facets, loading, error, reload }
}

// ---------------------------------------------------------------------
// useEmailFilterPresets — server-synced "Saved views" CRUD
// ---------------------------------------------------------------------
// Single hook that exposes the full CRUD surface; the host renders a
// dropdown of the loaded list and calls `save` / `update` / `remove`
// in response to user interactions. POST is idempotent on (user, org,
// name) — re-saving under the same name updates the existing row
// rather than 400-ing on the unique constraint, matching the FE's
// "Save as…" UX.
export function useEmailFilterPresets() {
  const [presets, setPresets] = useState<FilterPreset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await emailApi<FilterPreset[] | { results: FilterPreset[] }>(
        '/api/emails/filter-presets/',
      )
      const arr = Array.isArray(data) ? data : (data?.results ?? [])
      setPresets(arr)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load saved views')
      setPresets([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void reload() }, [reload])

  const save = useCallback(
    async (
      name: string,
      filters: Record<string, unknown>,
      isPinned = false,
    ): Promise<FilterPreset | null> => {
      try {
        const created = await emailApi<FilterPreset>('/api/emails/filter-presets/', {
          method: 'POST',
          body: JSON.stringify({ name, filters, is_pinned: isPinned }),
        })
        await reload()
        return created
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save view')
        return null
      }
    },
    [reload],
  )

  const update = useCallback(
    async (id: number, patch: Partial<FilterPreset>): Promise<FilterPreset | null> => {
      try {
        const updated = await emailApi<FilterPreset>(
          `/api/emails/filter-presets/${id}/`,
          {
            method: 'PATCH',
            body: JSON.stringify(patch),
          },
        )
        await reload()
        return updated
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update view')
        return null
      }
    },
    [reload],
  )

  const remove = useCallback(async (id: number): Promise<boolean> => {
    try {
      await emailApi<void>(`/api/emails/filter-presets/${id}/`, { method: 'DELETE' })
      await reload()
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete view')
      return false
    }
  }, [reload])

  return { presets, loading, error, reload, save, update, remove }
}

// ---------------------------------------------------------------------
// useEmailSyncStatus — live latest sync outcome for the sidebar
// ---------------------------------------------------------------------
// Polls /api/emails/sync/status/ on a low-frequency cadence so the
// EmailLayout footer can show "+12 synced from last 30d" or
// "Worker error: token expired" instead of leaving the user stuck on
// "Sync queued in background…" forever.
//
// Cadence:
//   - Every 8s while a sync is *active* (active_sync !== null)
//     so the user sees the row flip from `running` → `success` /
//     `error` within ~8s of the worker actually finishing.
//   - Every 60s while idle, as a heartbeat (catches periodic Celery
//     beat syncs and external dispatches without spamming the server).
//   - Refetches the moment the FE itself dispatches a queued chunk
//     (listens on EMAIL_SYNC_QUEUED_EVENT) so the visible state
//     transitions immediately from "Latest: idle" → "Running…".
//
// Cheap by design: indexed BE query against
// (organisation, -started_at), single row returned. Safe to leave
// running on every email-tab render — total overhead is well under
// the existing inbox-list and stats polls.
const SYNC_STATUS_ACTIVE_POLL_MS = 5_000
const SYNC_STATUS_IDLE_POLL_MS = 60_000

export function useEmailSyncStatus(enabled: boolean = true) {
  const [data, setData] = useState<EmailSyncStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Ref so the timer callback always reads the latest data without
  // forcing the effect to re-install its interval every poll.
  const activeRef = useRef(false)
  activeRef.current = !!data?.active_sync
  // Stack 13 MR-D — last-seen ``finished_at`` so we only fire the
  // EMAIL_LIST_INVALIDATE_EVENT when the BE's latest sync row
  // actually advanced. Without the ref we'd re-fire on every poll
  // (every 8s during active sync) and stack 4× the inbox refetches.
  const lastFinishedAtRef = useRef<string | null>(null)

  const reload = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    try {
      const next = await fetchEmailSyncStatus()
      setData(next)
      setError(null)
      // Stack 13 MR-D — emit the list-invalidate event when the BE
      // reports a fresh ``finished_at``. The check tolerates the
      // cold-mount case (first response sets the ref; no event
      // fires for what's already painted). Subsequent transitions
      // — manual sync done, Celery beat fired, external API
      // dispatched — flip the ref AND emit, which kicks every
      // ``useEmails`` instance in the tab into an immediate
      // refetch. The 30s background poll becomes the fallback
      // for "the sync-status polling never observed the change"
      // (e.g. the user opened the tab during a 60s idle window
      // and a sync completed inside that window before the next
      // 60s tick fired — caught on the following tick).
      const nextFinishedAt = next?.latest?.finished_at ?? null
      const previousFinishedAt = lastFinishedAtRef.current
      if (
        nextFinishedAt &&
        nextFinishedAt !== previousFinishedAt
      ) {
        lastFinishedAtRef.current = nextFinishedAt
        // Only EMIT on a real advance — the first observation
        // (previousFinishedAt === null) seeds the ref WITHOUT
        // firing, because the inbox the user is looking at was
        // already painted using rows that include the seeded
        // sync's results. Firing on cold mount would cause an
        // unnecessary refetch on every page load.
        if (previousFinishedAt !== null) {
          try {
            window.dispatchEvent(
              new CustomEvent(EMAIL_LIST_INVALIDATE_EVENT, {
                detail: { since: nextFinishedAt },
              }),
            )
          } catch {
            // CustomEvent unavailable on some legacy bridges /
            // SSR contexts. Silent — the 30s poll still catches
            // it.
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sync status')
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    void reload()
  }, [enabled, reload])

  // Adaptive interval: tight while a sync is running so the panel
  // refreshes within seconds of completion, relaxed otherwise.
  useEffect(() => {
    if (!enabled) return
    let timer: number | null = null
    const schedule = () => {
      const ms = activeRef.current ? SYNC_STATUS_ACTIVE_POLL_MS : SYNC_STATUS_IDLE_POLL_MS
      timer = window.setTimeout(async () => {
        if (!document.hidden) await reload()
        schedule()
      }, ms)
    }
    schedule()
    return () => { if (timer !== null) window.clearTimeout(timer) }
  }, [enabled, reload])

  // Refresh immediately when the FE dispatches a queued sync — the
  // row transitions to `running` within ms of the BE accepting the
  // POST, and we want the sidebar to reflect that without waiting
  // for the next poll tick.
  useEffect(() => {
    if (!enabled) return
    const onQueued = () => { void reload() }
    window.addEventListener(EMAIL_SYNC_QUEUED_EVENT, onQueued as EventListener)
    return () => window.removeEventListener(
      EMAIL_SYNC_QUEUED_EVENT, onQueued as EventListener,
    )
  }, [enabled, reload])

  return { data, loading, error, reload }
}
